/* ============================================================
   sc-gallery.js
   Pinterest-style gallery for the Sophie Conran prototype.

   - SC.gallery.items          : the curated dataset of lifestyle pins
   - SC.gallery.favourites     : localStorage-backed favourites set
   - SC.gallery.render(mount)  : paints the masonry grid into a node
   - SC.gallery.bindPage()     : wires up the gallery page (filters, save, etc.)
   - SC.gallery.openFavourites(): opens the favourites overlay
   - SC.gallery.downloadPDF()  : builds and downloads a favourites PDF

   Note on the dataset
   -------------------
   The cards below draw on the full Shopify lifestyle library already used
   across the prototype, broken out into individual fabric/range pins
   (the way customers think and pin: "I want the grey stripe Coco", not
   "I want a Coco"). When real organic-Pinterest top-performers are pulled
   in via the Windsor.ai connector, swap the `items` array — the schema is
   stable. Each item: { id, range, fabric, image, alt, price, weeks, swatch }.
   ============================================================ */
(function (global) {
  'use strict';
  var SC = global.SC = global.SC || {};
  SC.gallery = SC.gallery || {};

  /* ---------------------- DATA ---------------------- */
  /* The gallery dataset. Drawn from the existing image library so we get
     real photography, not placeholders. Each item is a "pin": one sofa,
     one fabric, one room. Price is the from-price for the size shown
     where known, else the range from-price. Weeks reflect UK lead time. */
  var CDN = 'https://cdn.shopify.com/s/files/1/0881/5227/7265/files/';

  var FALLBACK_ITEMS = [
    /* ---------------- COCO ---------------- */
    { id: 'coco-petunia',          range: 'Coco 2.5 seater',     fabric: 'Petunia Red Stripe',   priceFrom: 5800, weeks: 8,
      image: CDN + 'Sophie_Conran_Coco_2.5_Seater_Sofa_with_Self_Piped_Knife_Edge_cushions_in_Petunia_Red_Stripe.jpg?width=1200',
      alt: 'Coco 2.5 seater in Petunia Red Stripe', swatch: '#B56A4F', tags: ['sofas','stripes','red'] },

    { id: 'coco-garden-grey',      range: 'Coco 2.5 seater',     fabric: 'Stripes Garden Grey',  priceFrom: 5800, weeks: 8,
      image: CDN + 'Coco2.5seatersofawithknife-edgecushionsinstripeGardenGrey_B5A1964_75d78b39-f5e4-4a14-8d59-7312b94b99a5.jpg?width=1200',
      alt: 'Coco 2.5 seater in Stripes Garden Grey', swatch: '#A8B0AA', tags: ['sofas','stripes','grey'] },

    { id: 'coco-capelo-snow-drop',  range: 'Coco 2.5 seater',     fabric: 'Capelo Snow Drop', priceFrom: 5800, weeks: 8,
      image: 'https://cdn.sophieconran.com/renders/KCOS25K-O1421-FR-Coco-2.5%20Seater-Capelo%20Snow%20Drop.png',
      alt: 'Coco 2.5 Seater in Capelo Snow Drop', swatch: '#D8D4C5', tags: ['sofas','plain','neutral'] },

    { id: 'coco-wildwood-duck-egg', range: 'Coco 2.5 seater',    fabric: 'Wildwood Duck Egg',    priceFrom: 5800, weeks: 8,
      image: CDN + '1_72c6230f-e38d-4dd4-b7bb-9cbb5c76deab.jpg?width=1200',
      alt: 'Coco 2.5 seater in Wildwood Duck Egg', swatch: '#A8C0BC', tags: ['sofas','floral','blue'] },

    { id: 'coco-brera-chalk',      range: 'Coco 2.5 seater',     fabric: 'Brera Lino Chalk',     priceFrom: 5800, weeks: 8,
      image: CDN + '1_1c496a66-6205-4d00-9ac1-ea23a9915a93.jpg?width=1200',
      alt: 'Coco 2.5 seater in Brera Lino Chalk', swatch: '#E8E2D2', tags: ['sofas','plain','neutral'] },

    { id: 'coco-snow-drop',        range: 'Coco 2.5 seater',     fabric: 'Snow Drop',            priceFrom: 5800, weeks: 8,
      image: CDN + '1_425f6cc5-deef-4bbb-877c-93a7a5f0dcdc.jpg?width=1200',
      alt: 'Coco 2.5 seater in Snow Drop', swatch: '#EFE7D5', tags: ['sofas','plain','neutral'] },

    { id: 'coco-petunia-stripe',   range: 'Coco 2.5 seater',     fabric: 'Petunia Stripe',       priceFrom: 5800, weeks: 8,
      image: CDN + '1_6d7057f3-c2b9-453a-96c7-b354d91ccb40.jpg?width=1200',
      alt: 'Coco 2.5 seater in Petunia Stripe', swatch: '#B56A4F', tags: ['sofas','stripes','red'] },

    { id: 'coco-midsummer',        range: 'Coco 2.5 seater',     fabric: 'Midsummer Faded Blue', priceFrom: 5800, weeks: 8,
      image: CDN + '1_5084525e-6a9d-4045-a719-f9c26340d91a.jpg?width=1200',
      alt: 'Coco 2.5 seater in Midsummer Faded Blue', swatch: '#94A8B8', tags: ['sofas','floral','blue'] },

    { id: 'coco-sea-holly',        range: 'Coco 2.5 seater',     fabric: 'Stripes Sea Holly',    priceFrom: 5800, weeks: 8,
      image: CDN + '1_56ab5441-29a6-4f6e-9a65-74c5b1fbc07f.jpg?width=1200',
      alt: 'Coco 2.5 seater in Stripes Sea Holly', swatch: '#5F7A8C', tags: ['sofas','stripes','blue'] },

    { id: 'coco-wildwood-ivory',   range: 'Coco 2.5 seater',     fabric: 'Wildwood Ivory',       priceFrom: 5800, weeks: 8,
      image: CDN + '1_0a593c9a-c77a-49b6-9383-e86fdf030c7b.jpg?width=1200',
      alt: 'Coco 2.5 seater in Wildwood Ivory', swatch: '#E0D5C0', tags: ['sofas','floral','neutral'] },

    { id: 'coco-duck-egg-bespoke', range: 'Coco 2.5 seater',     fabric: 'Wildwood Duck Egg',    priceFrom: 5800, weeks: 8,
      image: CDN + 'Sophie_Conran_Coco_2.5_seater_sofa_with_knife-edge_cushions_in_Wild_Wood_Duck_Egg_B5A1260_e2ccfec6-bd1e-438a-b598-6f667238b6b9.jpg?width=1200',
      alt: 'Coco 2.5 seater in Wildwood Duck Egg, lifestyle', swatch: '#A8C0BC', tags: ['sofas','floral','blue'] },

    /* ---------------- BARTON ---------------- */
    { id: 'barton-grey-skies',     range: 'Barton 3 seater',     fabric: 'English Garden Grey Skies', priceFrom: 6720, weeks: 8,
      image: CDN + 'Salthrop_Sofa_with_self_piped_knife_edge_cushion_in_Grey_Skies_B5A6719.jpg?width=1200',
      alt: 'Barton 3 seater in English Garden Grey Skies', swatch: '#7E8B95', tags: ['sofas','floral','grey'] },

    { id: 'barton-berrington',     range: 'Barton 3 seater',     fabric: 'Stripe Berrington',    priceFrom: 6720, weeks: 8,
      image: CDN + 'Salthrop_Sofa_with_Self_Piped_Knife_Edge_cushion_in_Stripe_Berrington_JR4A3286_f5552c83-12c2-47c0-bff2-b3e59f08afb0.jpg?width=1200',
      alt: 'Barton 3 seater in Stripe Berrington', swatch: '#8B7A5E', tags: ['sofas','stripes','neutral'] },

    { id: 'barton-grey-skies-side',range: 'Barton 3 seater',     fabric: 'English Garden Grey Skies', priceFrom: 6720, weeks: 8,
      image: CDN + 'Salthrop_Sofa_with_self_piped_knife_edge_cushion_in_Grey_Skies_B5A6780.jpg?width=1200',
      alt: 'Barton 3 seater, side view, in English Garden Grey Skies', swatch: '#7E8B95', tags: ['sofas','floral','grey'] },

    { id: 'barton-sea-holly',      range: 'Barton 3 seater',     fabric: 'Stripes Sea Holly',    priceFrom: 6720, weeks: 8,
      image: CDN + 'salthrop_sofa_in_blue_stripe_jr4a3064.webp?width=1200',
      alt: 'Barton 3 seater in Stripes Sea Holly', swatch: '#5F7A8C', tags: ['sofas','stripes','blue'] },

    { id: 'barton-snow-drop',      range: 'Barton 3 seater',     fabric: 'Snow Drop',            priceFrom: 6720, weeks: 8,
      image: CDN + 'salthrop_sofa_jr4a2544.webp?width=1200',
      alt: 'Barton 3 seater in Snow Drop', swatch: '#EFE7D5', tags: ['sofas','plain','neutral'] },

    /* ---------------- ISOBEL ---------------- */
    { id: 'isobel-magnolia',       range: 'Isobel armchair',     fabric: 'GP&J Baker Magnolia Cream & Blue', priceFrom: 2150, weeks: 8,
      image: CDN + 'Sophie_Conran_Isobel_Armchair_with_contrast_piping_in_GP_J_Baker_Magnolia_Cream_and_Blue_-_1_Front_1.png?width=1200',
      alt: 'Isobel armchair in Magnolia Cream & Blue with contrast piping', swatch: '#D8C8B8', tags: ['armchairs','floral','blue'] },

    { id: 'isobel-duck-egg',       range: 'Isobel armchair',     fabric: 'Wildwood Duck Egg',    priceFrom: 2150, weeks: 8,
      image: CDN + 'Sophie_Conran_Isobel_Armchair_with_contrast_piping_in_Wildwood_Duck_Egg_-_1_Front.png?width=1200',
      alt: 'Isobel armchair in Wildwood Duck Egg with contrast piping', swatch: '#A8C0BC', tags: ['armchairs','floral','blue'] },

    { id: 'isobel-ivory',          range: 'Isobel armchair',     fabric: 'Wildwood Ivory',       priceFrom: 2150, weeks: 8,
      image: CDN + 'Sophie_Conran_Isobel_Armchair_with_contrast_piping_in_Wildwood_Ivory_-_1_Front_1.png?width=1200',
      alt: 'Isobel armchair in Wildwood Ivory', swatch: '#E0D5C0', tags: ['armchairs','floral','neutral'] },

    { id: 'isobel-pumpkin',        range: 'Isobel armchair',     fabric: 'Wildwood Pumpkin',     priceFrom: 2150, weeks: 8,
      image: CDN + 'SophieConranIsobelArmchairwithLooseBackCushioninWildwoodPumpkin_Front.jpg?width=1200',
      alt: 'Isobel armchair in Wildwood Pumpkin with loose back cushion', swatch: '#C87850', tags: ['armchairs','floral','orange'] },

    { id: 'isobel-snowdrop',       range: 'Isobel armchair',     fabric: 'Snow Drop',            priceFrom: 2150, weeks: 8,
      image: CDN + 'Sophie_Conran_Isobel_Armchair_with_self_piping_in_Snowdrop_-_1_Front.png?width=1200',
      alt: 'Isobel armchair in Snow Drop with self piping', swatch: '#EFE7D5', tags: ['armchairs','plain','neutral'] },

    /* ---------------- HENRIETTA ---------------- */
    { id: 'henrietta-1',           range: 'Henrietta sofa',      fabric: 'Lifestyle',            priceFrom: 4920, weeks: 8,
      image: CDN + 'SophieConranHenriettaSofaIMG_0057_1.jpg?width=1200',
      alt: 'Henrietta sofa, lifestyle', swatch: '#C5B098', tags: ['sofas','tailored','neutral'] },

    { id: 'henrietta-2',           range: 'Henrietta sofa',      fabric: 'Lifestyle',            priceFrom: 4920, weeks: 8,
      image: CDN + 'Sophie_Conran_Henrietta_Sofa_IMG_0090-HDR.jpg?width=1200',
      alt: 'Henrietta sofa, angled view', swatch: '#C5B098', tags: ['sofas','tailored','neutral'] },

    { id: 'henrietta-3',           range: 'Henrietta sofa',      fabric: 'Lifestyle',            priceFrom: 4920, weeks: 8,
      image: CDN + 'Sophie_Conran_Henrietta_Sofa_IMG_0087_1-HDR.jpg?width=1200',
      alt: 'Henrietta sofa, detail', swatch: '#C5B098', tags: ['sofas','tailored','neutral'] },

    { id: 'henrietta-armchair',    range: 'Henrietta armchair',  fabric: 'Lifestyle',            priceFrom: 2950, weeks: 8,
      image: CDN + 'HenriettaArmchairFrontView_1_1.png?width=1200',
      alt: 'Henrietta armchair, front view', swatch: '#C5B098', tags: ['armchairs','tailored','neutral'] },

    /* ---------------- FELIX ---------------- */
    { id: 'felix-marigold',        range: 'Felix 4 seater',      fabric: 'Cotton Linen Marigold',priceFrom: 5095, weeks: 7,
      image: CDN + 'Sophie_Conran_Felix_4_Seater_loose_cover_in_Cotton_Linen_Marigold_1_Front.png?width=1200',
      alt: 'Felix 4 seater in Cotton Linen Marigold', swatch: '#D89A3E', tags: ['sofas','plain','yellow'] },

    { id: 'felix-inkspot',         range: 'Felix 4 seater',      fabric: 'Cotton Linen Inkspot', priceFrom: 5095, weeks: 7,
      image: CDN + 'Sophie_Conran_Felix_4_Seater_loose_cover_in_Cotton_Linen_Inkspot_1_Front.png?width=1200',
      alt: 'Felix 4 seater in Cotton Linen Inkspot', swatch: '#1F2A38', tags: ['sofas','plain','navy'] },

    { id: 'felix-evergreen',       range: 'Felix 4 seater',      fabric: 'Cotton Linen Evergreen',priceFrom: 5095, weeks: 7,
      image: CDN + 'Sophie_Conran_Felix_4_Seater_loose_cover_in_Cotton_Linen_Evergreen_1_Front.png?width=1200',
      alt: 'Felix 4 seater in Cotton Linen Evergreen', swatch: '#3A5040', tags: ['sofas','plain','green'] },

    { id: 'felix-coconut',         range: 'Felix 4 seater',      fabric: 'Cotton Linen Cool Coconut',priceFrom: 5095, weeks: 7,
      image: CDN + 'Sophie_Conran_Felix_4_Seater_loose_cover_in_Cotton_Linen_Cool_Coconut_1_Front.png?width=1200',
      alt: 'Felix 4 seater in Cotton Linen Cool Coconut', swatch: '#E8DDC9', tags: ['sofas','plain','neutral'] },

    { id: 'felix-caramel',         range: 'Felix 4 seater',      fabric: 'Caramel Stripe',       priceFrom: 5095, weeks: 7,
      image: CDN + 'Sophie_Conran_Felix_4_Seater_loose_cover_in_Caramel_Stripe_1_Front.png?width=1200',
      alt: 'Felix 4 seater in Caramel Stripe', swatch: '#A87D5C', tags: ['sofas','stripes','neutral'] },

    { id: 'felix-pottingmoss',     range: 'Felix 4 seater',      fabric: 'Cotton Linen Potting Moss',priceFrom: 5095, weeks: 7,
      image: CDN + 'Sophie_Conran_Felix_4_Seater_loose_cover_in_Cotton_Linen_Potting_Moss_1_Front.png?width=1200',
      alt: 'Felix 4 seater in Cotton Linen Potting Moss', swatch: '#6F7A5A', tags: ['sofas','plain','green'] },

    { id: 'felix-emerald',         range: 'Felix 4 seater',      fabric: 'Little Magnolia Emerald',priceFrom: 5095, weeks: 7,
      image: CDN + 'Sophie_Conran_Felix_4_Seater_loose_cover_in_Little_Magnolia_Emerald_1_Front.png?width=1200',
      alt: 'Felix 4 seater in Little Magnolia Emerald', swatch: '#3D5A4A', tags: ['sofas','floral','green'] },

    { id: 'felix-espresso',        range: 'Felix 4 seater',      fabric: 'Little Magnolia Espresso',priceFrom: 5095, weeks: 7,
      image: CDN + 'Sophie_Conran_Felix_4_Seater_loose_cover_in_Little_Magnolia_Espresso_1_Front.png?width=1200',
      alt: 'Felix 4 seater in Little Magnolia Espresso', swatch: '#5A3A28', tags: ['sofas','floral','brown'] },

    /* ---------------- SOPHIA ---------------- */
    { id: 'sophia-snowdrop',       range: 'Sophia 3 seater',     fabric: 'Snow Drop',            priceFrom: 3450, weeks: 7,
      image: CDN + 'SophiaSofa-3-seater.png?width=1200',
      alt: 'Sophia 3 seater in Snow Drop', swatch: '#EFE7D5', tags: ['sofas','plain','neutral'] },

    { id: 'sophia-blossom',        range: 'Sophia 3 seater',     fabric: 'Blossom Stripe',       priceFrom: 3450, weeks: 7,
      image: CDN + 'Sophia_Sofa_-_3-seater_-_blossom.png?width=1200',
      alt: 'Sophia 3 seater in Blossom Stripe', swatch: '#E8C0A8', tags: ['sofas','stripes','pink'] },

    { id: 'sophia-duck-egg',       range: 'Sophia 3 seater',     fabric: 'Wildwood Duck Egg',    priceFrom: 3450, weeks: 7,
      image: CDN + 'Sophia_Sofa_-_3-seater_-_duck_egg.png?width=1200',
      alt: 'Sophia 3 seater in Wildwood Duck Egg', swatch: '#A8C0BC', tags: ['sofas','floral','blue'] },

    { id: 'sophia-inkspot',        range: 'Sophia 3 seater',     fabric: 'Cotton Linen Inkspot', priceFrom: 3450, weeks: 7,
      image: CDN + 'Sophia_Sofa_-_3-seater_-_inkspot.png?width=1200',
      alt: 'Sophia 3 seater in Cotton Linen Inkspot', swatch: '#1F2A38', tags: ['sofas','plain','navy'] },

    { id: 'sophia-ivory',          range: 'Sophia 3 seater',     fabric: 'Wildwood Ivory',       priceFrom: 3450, weeks: 7,
      image: CDN + 'Sophia_Sofa_-_3-seater_-_ivory.png?width=1200',
      alt: 'Sophia 3 seater in Wildwood Ivory', swatch: '#E0D5C0', tags: ['sofas','floral','neutral'] },

    { id: 'sophia-marigold',       range: 'Sophia 3 seater',     fabric: 'Cotton Linen Marigold',priceFrom: 3450, weeks: 7,
      image: CDN + 'Sophia_Sofa_-_3-seater_-_marigold.png?width=1200',
      alt: 'Sophia 3 seater in Cotton Linen Marigold', swatch: '#D89A3E', tags: ['sofas','plain','yellow'] },

    { id: 'sophia-putty',          range: 'Sophia 3 seater',     fabric: 'Putty',                priceFrom: 3450, weeks: 7,
      image: CDN + 'Sophia_Sofa_-_3-seater_-_putty.png?width=1200',
      alt: 'Sophia 3 seater in Putty', swatch: '#C5B098', tags: ['sofas','plain','neutral'] },

    /* ---------------- BED LINEN & PORTMEIRION (for cross-discovery) ---------------- */
    { id: 'bedlinen-blue',         range: 'Scalloped bed linen', fabric: 'Blue',                 priceFrom: 166.6, weeks: 0,
      image: CDN + 'blue-scalloped-bed-linen-collection_lifestyle-web-opt_5.webp?width=1200',
      alt: 'Blue scalloped bed linen, lifestyle', swatch: '#A8C0BC', tags: ['bed-linen','blue'] },

    { id: 'portmeirion-arbor',     range: 'Portmeirion Arbor',   fabric: 'Grey colourway',       priceFrom: 18, weeks: 0,
      image: CDN + 'arbor-grey-all-web-opt-lifestyle.webp?width=1200',
      alt: 'Sophie Conran for Portmeirion Arbor in grey', swatch: '#B5C9C5', tags: ['tabletop','grey'] },

    { id: 'portmeirion-lavandula', range: 'Portmeirion Lavandula',fabric: 'Place setting',       priceFrom: 22, weeks: 0,
      image: CDN + '749151794115_productlifestyle2_1_1.webp?width=1200',
      alt: 'Lavandula table runner and place setting', swatch: '#C0B5C8', tags: ['tabletop','purple'] },

    { id: 'portmeirion-cakestand', range: 'Portmeirion Lavandula',fabric: 'Footed Cake Stand',   priceFrom: 95, weeks: 0,
      image: CDN + '749151793347_productlifestyle1_1.webp?width=1200',
      alt: 'Lavandula footed cake stand', swatch: '#C0B5C8', tags: ['tabletop','purple'] }
  ];

  /* Use pre-loaded real dataset (sc-gallery-data.js) when present;
     fall back to the built-in placeholder set otherwise. */
  if (!Array.isArray(SC.gallery.items) || SC.gallery.items.length === 0) {
    SC.gallery.items = FALLBACK_ITEMS;
  }

  /* ---------------------- FAVOURITES ---------------------- */
  /* localStorage-backed favourites. Two stores side-by-side:
       FAV_KEY        : array of ids (referencing SC.gallery.items)
       CUSTOM_KEY     : object map { id: pinObject } for ad-hoc pins
                        saved from the PDP (the customer's current build)
     Both contribute to favourites.items() in the order they were added,
     newest first. */
  var FAV_KEY = 'sc_gallery_favourites';
  var CUSTOM_KEY = 'sc_gallery_custom_pins';

  /* In-memory registry of "known" pins from places other than the
     static SC.gallery.items list (e.g. Portmeirion bestsellers,
     bed linen tiles, in-stock items). Pages register their pins
     at boot via SC.gallery.registerPins([...]); the favourites
     items() lookup then resolves saved IDs against this registry,
     so the overlay can render them even when the user navigates
     to a different page and reopens the heart icon.

     This registry lives in memory only (no localStorage) because
     each page knows its own pin shapes and registers them on
     load. localStorage is reserved for user data: the FAV id
     list, and custom PDP pins built by the configurator. */
  var registry = {};

  function readCustom() {
    try { return JSON.parse(localStorage.getItem(CUSTOM_KEY) || '{}'); }
    catch (e) { return {}; }
  }
  function writeCustom(obj) {
    localStorage.setItem(CUSTOM_KEY, JSON.stringify(obj));
  }

  /* Register an array of pin objects so the favourites overlay can
     find them by ID. Idempotent: re-registering the same pin
     overwrites the entry. Pages call this at boot. */
  SC.gallery.registerPins = function(pins) {
    if (!pins || !pins.length) return;
    pins.forEach(function(p) {
      if (p && p.id) registry[p.id] = p;
    });
  };

  SC.gallery.favourites = {
    list: function() {
      try { return JSON.parse(localStorage.getItem(FAV_KEY) || '[]'); }
      catch (e) { return []; }
    },
    has: function(id) {
      return this.list().indexOf(id) !== -1;
    },
    add: function(id) {
      var l = this.list();
      if (l.indexOf(id) === -1) {
        l.push(id);
        localStorage.setItem(FAV_KEY, JSON.stringify(l));
        /* Promote any registered pin into the custom-pins store on
           save, so the overlay can still find it if the user later
           opens the favourites from a page that did not register it
           (e.g. saves a Portmeirion piece on the bestsellers page,
           then opens the header heart from the Coco PDP). */
        if (registry[id]) {
          var custom = readCustom();
          if (!custom[id]) {
            custom[id] = registry[id];
            writeCustom(custom);
          }
        }
        emit();
      }
    },
    remove: function(id) {
      var l = this.list().filter(function(x){ return x !== id; });
      localStorage.setItem(FAV_KEY, JSON.stringify(l));
      /* Also drop any custom pin record for this id */
      var custom = readCustom();
      if (custom[id]) {
        delete custom[id];
        writeCustom(custom);
      }
      emit();
    },
    toggle: function(id) {
      if (this.has(id)) this.remove(id); else this.add(id);
    },
    clear: function() {
      localStorage.removeItem(FAV_KEY);
      localStorage.removeItem(CUSTOM_KEY);
      emit();
    },
    /* Save a custom pin (e.g. from the PDP configurator). The pin object
       should have at minimum: { id, range, fabric, image, alt, priceFrom,
       weeks, swatch, tags, source }. The id should be stable for the
       same build so toggling works. */
    addCustom: function(pin) {
      if (!pin || !pin.id) return;
      var custom = readCustom();
      custom[pin.id] = pin;
      writeCustom(custom);
      this.add(pin.id);
    },
    /* Counts */
    count: function() { return this.list().length; },
    items: function() {
      var ids = this.list();
      var map = {};
      SC.gallery.items.forEach(function(it){ map[it.id] = it; });
      var custom = readCustom();
      return ids.map(function(id){
        /* Resolution order: static gallery items → custom PDP pins
           → in-memory registry (Portmeirion / bed linen / etc).
           Anything missing from all three is filtered out. */
        return map[id] || custom[id] || registry[id] || null;
      }).filter(Boolean);
    }
  };

  function emit() {
    document.dispatchEvent(new CustomEvent('sc:favourites-changed'));
    /* Mirror the full favourite objects into the synced store so they
       reach the Worker and other devices. Only pushes when signed in;
       SC.saves.setFavouriteObjects no-ops the network call otherwise. */
    if (SC.saves && SC.saves.isSignedIn && SC.saves.isSignedIn() && SC.saves.setFavouriteObjects) {
      try { SC.saves.setFavouriteObjects(SC.gallery.favourites.items()); } catch (e) {}
    }
  }

  /* ── Bridge for sc-saves.js cross-device sync ──
     _collectFavourites(): hand the synced layer the full pin objects this
       device currently holds, so sign-in captures pre-sign-in favourites.
     _applyFavourites(objs): take merged objects from the server and
       rehydrate this device's stores so the overlay renders them. */
  if (SC.saves) {
    SC.saves._collectFavourites = function() {
      try { return SC.gallery.favourites.items(); } catch (e) { return []; }
    };
    SC.saves._applyFavourites = function(objects) {
      objects = Array.isArray(objects) ? objects : [];
      var ids = [];
      var custom = readCustom();
      objects.forEach(function(o) {
        if (!o || !o.id) return;
        ids.push(o.id);
        /* Stash the full payload so items() can resolve it on any page */
        custom[o.id] = o;
      });
      writeCustom(custom);
      localStorage.setItem(FAV_KEY, JSON.stringify(ids));
      document.dispatchEvent(new CustomEvent('sc:favourites-changed'));
      /* If the overlay is open, re-render it in place */
      if (document.querySelector('.sc-fav-overlay.is-open')) {
        SC.gallery.closeFavourites();
        SC.gallery.openFavourites();
      }
    };
  }

  /* ---------------------- FORMATTERS ---------------------- */
  function priceLabel(item) {
    if (item.priceFrom == null) return '';
    var p = item.priceFrom;
    var prefix = (item.tags && (item.tags.indexOf('bed-linen') !== -1 || item.tags.indexOf('tabletop') !== -1)) ? 'From £' : '£';
    if (p % 1 === 0) return prefix + p.toLocaleString('en-GB');
    return prefix + p.toFixed(2);
  }

  function leadLabel(item) {
    if (!item.weeks || item.weeks === 0) return 'Ready to ship';
    return 'Ready in ' + item.weeks + ' weeks';
  }

  function isFurniture(item) {
    return !(item.tags && (item.tags.indexOf('bed-linen') !== -1 || item.tags.indexOf('tabletop') !== -1));
  }

  /* ---------------------- CARD RENDERER ---------------------- */
  /* Render a saved build (from SC.saves) as a card that matches the
     favourites pin styling, but with Open / Remove actions instead of a
     heart toggle. Saved builds are higher-intent than favourites, so the
     overlay shows them first. */
  SC.gallery.buildCard = function(b) {
    var title = b.rangeName || b.range || 'Sofa';
    var fabricName = b.fabricName || b.fabric || '';
    var sizeBit = b.size ? '<span class="sc-pin-build-size">' + b.size + '</span>' : '';
    var priceTxt = b.price ? '£' + Number(b.price).toLocaleString('en-GB') : '';
    var imgHtml = b.imageUrl
      ? '<img class="sc-pin-image sc-pin-image-contain" src="' + b.imageUrl + '" alt="' + title + '" loading="lazy" ' +
             'onload="var c=this.closest(\'.sc-pin\'); if(c) c.classList.add(\'sc-pin-loaded\');" ' +
             'onerror="var p=this.closest(\'.sc-pin\'); if(p) p.style.display=\'none\';" />'
      : '<div class="sc-pin-image sc-pin-image-placeholder" aria-hidden="true"></div>';
    var openUrl = (SC.saves && SC.saves.buildShareUrl) ? SC.saves.buildShareUrl(b) : '#';
    /* A hearted-from-PDP build lives in favourites (remove via favId); a
       "Save & share" build lives in saved builds (remove via id). */
    var removeAttr = b.isFavBuild
      ? 'data-sc-fav-remove="' + b.favId + '"'
      : 'data-sc-build-remove="' + (b.id || '') + '"';
    var cardId = b.isFavBuild ? b.favId : (b.id || '');
    /* Builds without a render image use a placeholder <div> that can't fire
       onload, so mark them loaded immediately so the caption + Open link show. */
    var loadedClass = b.imageUrl ? '' : ' sc-pin-loaded';
    return '' +
      '<article class="sc-pin sc-pin-build' + loadedClass + '" data-build-id="' + cardId + '">' +
        '<div class="sc-pin-image-wrap">' +
          imgHtml +
          '<span class="sc-pin-build-badge">Your build</span>' +
          '<button class="sc-pin-build-remove" type="button" aria-label="Remove saved build" ' + removeAttr + '>' +
            '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12"/><path d="M18 6L6 18"/></svg>' +
          '</button>' +
        '</div>' +
        '<div class="sc-pin-caption">' +
          '<h3 class="sc-pin-name">' + title + ' ' + sizeBit + '</h3>' +
          (fabricName ? '<div class="sc-pin-fabric"><span class="sc-pin-fabric-name">' + fabricName + '</span></div>' : '') +
          '<div class="sc-pin-divider"></div>' +
          '<div class="sc-pin-foot">' +
            '<div class="sc-pin-price-wrap">' +
              '<div class="sc-pin-price">' + priceTxt + '</div>' +
              '<div class="sc-pin-mto">Your configuration</div>' +
            '</div>' +
            '<a class="sc-pin-build-open" href="' + openUrl + '">Open &rsaquo;</a>' +
          '</div>' +
          '<button class="sc-pin-build-basket" type="button" data-sc-build-basket="' + cardId + '">Add to basket</button>' +
        '</div>' +
      '</article>';
  };

  /* This is the centrepiece. Matches the two reference screenshots
     pixel-for-pixel: full-bleed lifestyle image on top, cream caption
     block underneath with name, swatch + fabric, divider, price + lead
     time, "Made to order" italic note. */
  SC.gallery.card = function(item, opts) {
    opts = opts || {};
    var isFav = SC.gallery.favourites.has(item.id);
    /* Real pixel dimensions, if the dataset carries them (w/h preferred, but
       accept width/height too). Emitting them on the <img> lets the browser
       reserve the correctly-shaped box per pin before load: no column
       reflow (no jumping) and true mixed portrait/landscape (no crop). */
    var iw = item.w || item.width, ih = item.h || item.height;
    var dimsAttr = (iw && ih) ? 'width="' + iw + '" height="' + ih + '" ' : '';
    return '' +
      '<article class="sc-pin" data-pin-id="' + item.id + '">' +
        '<div class="sc-pin-image-wrap">' +
          '<img class="sc-pin-image" src="' + item.image + '" alt="' + item.alt + '" loading="lazy" decoding="async" ' +
               dimsAttr +
               'onload="var c=this.closest(\'.sc-pin\'); if(c) c.classList.add(\'sc-pin-loaded\');" ' +
               'onerror="var p=this.closest(\'.sc-pin\'); if(p) p.style.display=\'none\';" />' +
          '<button class="sc-pin-save' + (isFav ? ' is-saved' : '') + '" type="button" ' +
                  'aria-label="' + (isFav ? 'Remove from saved' : 'Save to favourites') + '" ' +
                  'aria-pressed="' + (isFav ? 'true' : 'false') + '" ' +
                  'data-sc-pin-save="' + item.id + '">' +
            '<svg class="sc-pin-heart" width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">' +
              '<path d="M12 21s-7.5-4.5-9.5-9.5C1 7 4 4 7.5 4c2 0 3.5 1 4.5 2.5C13 5 14.5 4 16.5 4 20 4 23 7 21.5 11.5 19.5 16.5 12 21 12 21z" ' +
                'fill="' + (isFav ? 'currentColor' : 'none') + '" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>' +
            '</svg>' +
          '</button>' +
        '</div>' +
        '<div class="sc-pin-caption">' +
          '<h3 class="sc-pin-name">' + item.range + '</h3>' +
          '<div class="sc-pin-fabric">' +
            '<span class="sc-pin-swatch" style="background:' + item.swatch + ';"></span>' +
            '<span class="sc-pin-fabric-name">' + item.fabric + '</span>' +
          '</div>' +
        '</div>' +
      '</article>';
  };

  /* ---------------------- RENDER ---------------------- */
  /* ── Masonry ──
     The grid used to be CSS multi-column, which fills top-to-bottom and
     only then left-to-right. With 300 pins across 5 columns each column
     holds about 60, so the first 24 pins all stacked down the left-hand
     column and the visible top row was pins 1, 67, 127, 186 and 244. Any
     deliberate ordering was correct in the DOM and invisible on screen.

     Packing the columns here instead means order is honoured the way the
     page is actually read: each pin goes to whichever column is currently
     shortest, so pins 1..n fill the top row left to right and everything
     after simply continues into the same columns. No seam, because there
     is only one grid.

     Column count and gap stay in the CSS media queries and are read back
     from the computed style, so the breakpoints have one home. */
  function columnCountFor(mount) {
    var n = parseInt(getComputedStyle(mount).columnCount, 10);
    return (n && !isNaN(n)) ? n : 1;
  }

  /* Rough card height, used only to decide which column is shortest.
     Driven by each pin's own aspect ratio, plus a constant for the caption
     block beneath it. It does not need to be exact to balance well. */
  var CAPTION_H = 84;
  function estimateHeight(item, colWidth) {
    var ratio = (item.w && item.h) ? (item.h / item.w) : 1;
    return colWidth * ratio + CAPTION_H;
  }

  SC.gallery.layoutMasonry = function(mount, items) {
    var cols = columnCountFor(mount);
    mount.innerHTML = '';

    var colEls = [], heights = [];
    for (var i = 0; i < cols; i++) {
      var c = document.createElement('div');
      c.className = 'sc-gallery-col';
      mount.appendChild(c);
      colEls.push(c);
      heights.push(0);
    }

    /* Only the ratio between card heights matters here, not the absolute
       size, so a nominal width is fine when the grid has no layout yet:
       hidden behind another view, or rendered before first paint. Falling
       back to 1 would let the constant caption height swamp the aspect
       ratio and flatten balancing into plain round-robin. A re-render
       happens with real widths as soon as the view is shown. */
    var colWidth = colEls[0].clientWidth || (mount.clientWidth / cols) || 300;

    items.forEach(function(it) {
      var k = 0;
      for (var j = 1; j < cols; j++) {
        if (heights[j] < heights[k]) k = j;
      }
      colEls[k].insertAdjacentHTML('beforeend', SC.gallery.card(it));
      heights[k] += estimateHeight(it, colWidth);
    });

    mount.setAttribute('data-cols', cols);
  };

  SC.gallery.render = function(mountId, items) {
    items = items || SC.gallery.items;
    var mount = document.getElementById(mountId);
    if (!mount) return;

    if (mountId === 'gallery-mount') {
      /* Lead with what sells, de-clustered as it goes. rankForSelling folds
         in the shoot key, so it replaces spreadShoots here rather than
         running alongside it. */
      items = SC.gallery.rankForSelling(items);
      if (items.length === 0) {
        mount.innerHTML =
          '<div class="sc-gallery-empty">' +
            '<p class="sc-lede">No matches in this filter. Try another.</p>' +
          '</div>';
        return;
      }
      SC.gallery.layoutMasonry(mount, items);
      SC.gallery._lastItems = items;
      return;
    }

    if (items.length === 0) {
      mount.innerHTML =
        '<div class="sc-gallery-empty">' +
          '<p class="sc-lede">No matches in this filter. Try another.</p>' +
        '</div>';
      return;
    }
    mount.innerHTML = items.map(function(it){ return SC.gallery.card(it); }).join('');
  };

  /* Re-pack when the breakpoint changes the column count. Width changes
     within a breakpoint do not need it: the columns are flexible. */
  var resizeTimer = null;
  window.addEventListener('resize', function() {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function() {
      var mount = document.getElementById('gallery-mount');
      if (!mount || !SC.gallery._lastItems || !SC.gallery._lastItems.length) return;
      if (String(columnCountFor(mount)) === mount.getAttribute('data-cols')) return;
      SC.gallery.layoutMasonry(mount, SC.gallery._lastItems);
      if (SC.rewriteAssetPaths) SC.rewriteAssetPaths();
    }, 180);
  });
  /* ---------------------- SELLING ORDER ---------------------- */
  /* The grid leads with what actually sells, without becoming a wall of the
     same sofa. Edit these three lists to re-weight it; nothing else needs
     to change. Matching is substring and case-insensitive, so "wildwood"
     catches both Wildwood Duck Egg and Wildwood Ivory.

     Set SC.gallery.RANKING before the page paints to override, e.g. from a
     manifest, without touching this file. */
  SC.gallery.RANKING = SC.gallery.RANKING || {
    /* Hand-picked pins that lead the grid, in this exact order, before the
       scoring below takes over. Scoring can tell a best-selling sofa from
       an also-ran, but it cannot tell a hero shot from a rear or partly
       obstructed one, so the first screenful is chosen by eye.

       Pin ids, set from the section setting or a manifest. Ids that are not
       in the current view are skipped, so one list is safe across the
       range chips and the Customer Rooms tab. */
    featured: [],
    ranges:  ['coco'],
    sizes:   ['2.5 seater', '3 seater'],
    fabrics: ['wildwood', 'petunia', 'snowdrop', 'snow drop', 'chalk'],
    weights: { range: 4, size: 3, fabric: 3 },
    /* Accessories and non-sofa lines still belong in the gallery, but a
       cushion in a best-selling fabric would otherwise score as highly as
       the sofa it sits on and crowd the top of the page. Matched against
       range and size together. */
    demote: ['cushion', 'side table', 'footstool', 'tables & props', 'bed'],
    demoteWeight: 5,
    /* Repetition cost. Each time a combination (or a shoot) is used, every
       remaining pin sharing it loses this much, so the grid works through
       all the strong fabrics before coming back to one. Raise for more
       variety, lower to let the very best sellers cluster harder. A simple
       "don't repeat the last two" rule is not enough on its own: with a
       handful of top-scoring fabrics it just cycles the same three. */
    repeatPenalty: { variety: 1.5, shoot: 1.0 }
  };

  function sellingScore(item) {
    var R = SC.gallery.RANKING;
    var score = 0;
    var range = (item.range || '').toLowerCase();
    var size = (item.size || '').toLowerCase();
    var fabric = (item.fabric || '').toLowerCase();
    if (R.ranges.some(function(r){ return range.indexOf(r) !== -1; }))  score += R.weights.range;
    if (R.sizes.some(function(s){ return size.indexOf(s) !== -1; }))    score += R.weights.size;
    if (R.fabrics.some(function(f){ return fabric.indexOf(f) !== -1; })) score += R.weights.fabric;
    if (R.demote && R.demote.length) {
      var what = range + ' ' + size;
      if (R.demote.some(function(d){ return what.indexOf(d) !== -1; })) score -= (R.demoteWeight || 0);
    }
    return score;
  }

  /* What counts as "the same thing again" for variety purposes: the same
     sofa in the same cloth, or another frame from the same shoot. */
  function varietyKey(item) {
    return [(item.range || ''), (item.size || ''), (item.fabric || '')].join('|').toLowerCase();
  }

  /* Rank by selling score, placing one pin at a time and charging a cost
     for anything already used. Best sellers lead the grid, but the ordering
     works across every strong fabric before returning to the first, so the
     top of the page is not one sofa repeated.

     Replaces spreadShoots on the main grid rather than running before it:
     the shoot key is charged in the same pass, and round-robin spreading
     afterwards would undo the ranking. */
  SC.gallery.rankForSelling = function(items) {
    var R = SC.gallery.RANKING;
    var pen = R.repeatPenalty || { variety: 1.5, shoot: 1.0 };
    items = items || [];

    /* Hand-picked pins first, in the order they were chosen, and exempt
       from the repetition rules: if someone has deliberately put two Coco
       Snowdrops side by side, that is the decision, not an accident. */
    var lead = [], rest = items;
    if (R.featured && R.featured.length) {
      var byId = {};
      items.forEach(function(it){ if (it && it.id) byId[it.id] = it; });
      var taken = {};
      R.featured.forEach(function(id){
        if (byId[id] && !taken[id]) { lead.push(byId[id]); taken[id] = true; }
      });
      rest = items.filter(function(it){ return !(it && taken[it.id]); });
    }

    var pool = rest.map(function(it, i){
      return { it: it, score: sellingScore(it), i: i, vk: varietyKey(it), sk: shootKeyOf(it) };
    });

    /* Seed the counters from the hand-picked run so the pin straight after
       it is not a fourth Coco Snowdrop. */
    var usedV = {}, usedS = {}, out = lead.slice();
    lead.forEach(function(it){
      var vk = varietyKey(it), sk = shootKeyOf(it);
      usedV[vk] = (usedV[vk] || 0) + 1;
      usedS[sk] = (usedS[sk] || 0) + 1;
    });
    while (pool.length) {
      var best = 0, bestVal = -Infinity;
      for (var n = 0; n < pool.length; n++) {
        var p = pool[n];
        var val = p.score
                - (usedV[p.vk] || 0) * pen.variety
                - (usedS[p.sk] || 0) * pen.shoot;
        /* Ties keep the curated order from the manifest. */
        if (val > bestVal) { bestVal = val; best = n; }
      }
      var chosen = pool.splice(best, 1)[0];
      out.push(chosen.it);
      usedV[chosen.vk] = (usedV[chosen.vk] || 0) + 1;
      usedS[chosen.sk] = (usedS[chosen.sk] || 0) + 1;
    }
    return out;
  };

  /* ---------------------- SHOOT SPREADING ---------------------- */
  /* Pins share a shoot when their filenames differ only by frame number.
     We don't group them into albums (that tested worse); instead we
     interleave so at most MAX_ADJACENT from one shoot appear together,
     then the shoot recurs later. Pure single-photo flow, just de-clustered. */
  var MAX_ADJACENT = 2;

  function shootKeyOf(item) {
    var src = item.imageLightbox || item.image || '';
    var file = src.split('/').pop().split('?')[0];
    return file.replace(/-\d+\.[a-z]+$/i, '');
  }
  SC.gallery.shootKeyOf = shootKeyOf;

  SC.gallery.spreadShoots = function(items) {
    items = items || [];
    /* Bucket by shoot, preserving original order within each shoot. */
    var buckets = {}, order = [];
    items.forEach(function(it){
      var k = shootKeyOf(it);
      if (!buckets[k]) { buckets[k] = []; order.push(k); }
      buckets[k].push(it);
    });
    /* Round-robin: take up to MAX_ADJACENT from each shoot per pass, looping
       until all are placed. This naturally spaces a shoot's photos apart. */
    var out = [], remaining = true;
    while (remaining) {
      remaining = false;
      for (var i = 0; i < order.length; i++) {
        var b = buckets[order[i]];
        if (!b.length) continue;
        for (var n = 0; n < MAX_ADJACENT && b.length; n++) out.push(b.shift());
        if (b.length) remaining = true;
      }
    }
    return out;
  };

  /* ---------------------- LIGHTBOX ---------------------- */

  /* ── Pin → configurator deep link ──
     Gallery pins carry DISPLAY names (range "Coco", fabric "DG Brera Lino
     Chalk", size "3 Seater Sofa"). The configurator PDP expects option
     SLUGS via URL params (?size=3-seater&fabric=brera-lino-chalk), which
     parseUrlConfig() already reads. This resolver maps one to the other.
     Returns null when a pin can't be mapped (lifestyle shots, unknown
     fabrics) so callers can simply not render the CTA. */

  /* Range name → PDP path. Defaults assume the product handle matches the
     lowercased range name; override any entry from the console or a future
     CMS setting via SC.gallery.rangeUrls before first lightbox open. */
  SC.gallery.rangeUrls = SC.gallery.rangeUrls || {
    'coco':      '/products/coco',
    'barton':    '/products/barton',
    'henrietta': '/products/henrietta',
    'isobel':    '/products/isobel',
    'felix':     '/products/felix',
    'sophia':    '/products/sophia'
  };

  SC.gallery.resolveConfigLink = function(item) {
    if (!item || item.lifestyleOnly) return null;
    var rangeKey = (item.range || '').trim().toLowerCase();
    if (!rangeKey || !SC.gallery.rangeUrls[rangeKey]) return null;

    /* Size: "3 Seater Sofa" → "3-seater"; "2.5 Seater" → "2.5-seater";
       "Armchair" → "armchair"; "Loveseat" → "loveseat". */
    var sizeSlug = null;
    var sizeRaw = (item.size || '').toLowerCase();
    /* Armchair and Loveseat first: pin labels like "Loveseat (1.5 Seater)"
       must resolve to the theme's internal value ('loveseat'), not the
       calculator's ('1.5-seater') — the pricing client translates at the
       payload boundary. */
    if (sizeRaw.indexOf('armchair') !== -1) sizeSlug = 'armchair';
    else if (sizeRaw.indexOf('loveseat') !== -1) sizeSlug = 'loveseat';
    else {
      var mSeater = sizeRaw.match(/(\d+(?:\.\d+)?)\s*seater/);
      if (mSeater) sizeSlug = mSeater[1] + '-seater';
    }

    /* Fabric: strip mill prefixes ("DG ", "RM "...), slugify, and verify
       against SC.fabrics. Fallback: case-insensitive match on the display
       name so "DG Brera Lino Chalk" finds { name: 'Brera Lino Chalk' }. */
    var fabricHandle = null;
    var fabrics = SC.fabrics || {};
    if (item.fabric) {
      var cleaned = item.fabric.trim().replace(/^[A-Z]{2,3}\s+/, '');
      var slug = cleaned.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
      if (fabrics[slug]) fabricHandle = slug;
      if (!fabricHandle) {
        var target = cleaned.toLowerCase();
        Object.keys(fabrics).some(function(h) {
          var n = (fabrics[h].name || '').toLowerCase();
          if (n === target || target.indexOf(n) !== -1 || n.indexOf(target) !== -1) {
            fabricHandle = h; return true;
          }
          return false;
        });
      }
    }

    var params = [];
    if (sizeSlug) params.push('size=' + encodeURIComponent(sizeSlug));
    if (fabricHandle) params.push('fabric=' + encodeURIComponent(fabricHandle));
    return {
      rangeKey: rangeKey,
      rangeName: item.range,
      sizeSlug: sizeSlug,
      sizeLabel: item.size || '',
      fabricHandle: fabricHandle,
      fabricName: fabricHandle && fabrics[fabricHandle] ? fabrics[fabricHandle].name : (item.fabric || ''),
      url: SC.gallery.rangeUrls[rangeKey] + (params.length ? '?' + params.join('&') : '')
    };
  };

  /* ── Add to basket straight from the gallery ──
     Prices the pin's build (range + size + fabric, defaults elsewhere)
     through the same Vercel endpoint the PDP uses, then adds it to the
     unified basket. If no price comes back the button quietly hands over
     to the configurator link instead — never a dead end, never a wrong
     price. Market comes from the page's data-sc-market attribute exactly
     as the configurator reads it; absent means UK. */
  SC.gallery.addPinToBasket = function(item, btnEl) {
    var link = SC.gallery.resolveConfigLink(item);

    function fail(msg) {
      /* No redirect: the lightbox already offers "See it on the sofa
         builder" alongside this button. */
      if (btnEl) {
        btnEl.disabled = false;
        btnEl.textContent = msg;
        setTimeout(function(){ btnEl.textContent = 'Add to basket'; }, 2600);
      }
    }

    if (!link) { fail('Unavailable'); return; }
    if (!(SC.pricingApi && SC.pricingApi.getPrice && SC.cart && SC.cart._addConfiguredSofa)) {
      console.warn('[SC gallery] SC.pricingApi or SC.cart unavailable on this page. ' +
        'Check sc-pricing-api.js is loaded for this template in theme.liquid.');
      fail('Unavailable');
      return;
    }
    if (btnEl) { btnEl.disabled = true; btnEl.textContent = 'Adding\u2026'; }

    /* A pin knows only range, size and fabric. Seed the rest from the
       range defaults so the basket entry describes exactly the
       configuration that was priced, not a partial one. */
    var marketEl = document.querySelector('[data-sc-market]');
    var market = (marketEl && marketEl.getAttribute('data-sc-market') === 'US') ? 'US' : 'UK';
    var seeded = SC.gallery.completeSelection(
      link.rangeKey, { size: link.sizeSlug }, market);

    var build = {
      range: link.rangeKey,
      rangeName: link.rangeName,
      size: link.sizeSlug,
      fabric: link.fabricHandle,
      fabricName: link.fabricName,
      cushion: seeded.cushion, cover: seeded.cover, piping: seeded.piping,
      cushionFill: seeded.cushionFill, legStyle: seeded.legStyle,
      legFinish: seeded.legFinish, frame: seeded.frame
    };

    SC.gallery.priceBuild(build).then(function(price) {
      if (price == null) {
        console.warn('[SC gallery] No price returned for this pin.', link);
        fail('Price on request');
        return;
      }
      SC.cart._addConfiguredSofa(
        SC.gallery._sofaEntry(build, price, item.image || '',
          window.location.origin + link.url));
      SC.gallery._afterAdd(btnEl);
    });
  };

  /* ── Complete a partial selection into something priceable ──
     The calculator needs every option group filled, not just the two or
     three a gallery card knows about. The configurator seeds these on
     load (market-filtered, isDefault or first non-bespoke); outside it,
     nothing did, so pricing returned available:false.

     This mirrors that seeding, then overlays rules.fixed (ranges like
     Felix and Isobel define cushion/backCushion as fixed rather than
     selectable), then overlays whatever the caller actually knows.
     Caller values always win. */
  SC.gallery.completeSelection = function(rangeKey, partial, market) {
    var out = {};
    var rules = (SC.configurationRules || {})[(rangeKey || '').toLowerCase()];
    partial = partial || {};

    if (rules && rules.groups) {
      rules.groups.forEach(function(group) {
        var opts = group.options.filter(function(o) {
          if (!o.markets) return true;
          return o.markets.indexOf(market) !== -1;
        });
        if (!opts.length) return;
        var pick = null;
        if (group.key === 'size' && rules.defaultSize) {
          pick = opts.filter(function(o){ return o.value === rules.defaultSize; })[0];
        }
        if (!pick) pick = opts.filter(function(o){ return o.isDefault; })[0];
        if (!pick) pick = opts.filter(function(o){ return !o.bespoke; })[0] || opts[0];
        if (pick) out[group.key] = pick.value;
      });
    }

    /* Fixed (non-choosable) attributes for this range. */
    if (rules && rules.fixed) {
      Object.keys(rules.fixed).forEach(function(k) {
        if (out[k] == null) out[k] = rules.fixed[k].value;
      });
    }

    /* Known values win over seeded defaults. Empty strings count as
       unknown: saved builds store '' for options never chosen. */
    Object.keys(partial).forEach(function(k) {
      if (partial[k] !== null && partial[k] !== undefined && partial[k] !== '') {
        out[k] = partial[k];
      }
    });
    if (!out.frame) out.frame = 'standard';
    return out;
  };

  /* Price cache + in-flight dedupe.

     Two savings, both real on this page: several saved builds are often
     the same configuration (the same Coco loveseat saved twice), and the
     carousel re-renders whenever builds change. Keyed by the full payload
     so a cache hit is always an exact configuration match, market
     included. sessionStorage rather than localStorage: prices should not
     survive beyond the visit. */
  var _priceMem = {};
  var _priceInflight = {};
  var PRICE_STORE_KEY = 'sc_price_cache_v1';

  function _priceStoreRead() {
    try {
      var raw = sessionStorage.getItem(PRICE_STORE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch (e) { return {}; }
  }
  function _priceStoreWrite(key, value) {
    try {
      var all = _priceStoreRead();
      all[key] = value;
      sessionStorage.setItem(PRICE_STORE_KEY, JSON.stringify(all));
    } catch (e) { /* private mode or quota: memory cache still applies */ }
  }

  /* Price a saved build. Resolves to a number, or null if unavailable.
     Used both by the basket buttons and by the price shown on cards. */
  SC.gallery.priceBuild = function(build) {
    if (!build || !(SC.pricingApi && SC.pricingApi.getPrice)) {
      return Promise.resolve(null);
    }
    var marketEl = document.querySelector('[data-sc-market]');
    var market = (marketEl && marketEl.getAttribute('data-sc-market') === 'US') ? 'US' : 'UK';
    var sel = SC.gallery.completeSelection(build.range, {
      size: build.size, cushion: build.cushion, backCushion: build.backCushion,
      cover: build.cover, piping: build.piping, cushionFill: build.cushionFill,
      legStyle: build.legStyle, legFinish: build.legFinish, frame: build.frame
    }, market);
    sel.fabricHandle = build.fabric || null;

    var payload = SC.pricingApi.buildPayload(sel, (build.range || '').toLowerCase(), market);
    var key = JSON.stringify(payload);

    /* 1. Already resolved this visit. */
    if (Object.prototype.hasOwnProperty.call(_priceMem, key)) {
      return Promise.resolve(_priceMem[key]);
    }
    /* 2. Seed memory from sessionStorage on first ask. */
    var stored = _priceStoreRead();
    if (Object.prototype.hasOwnProperty.call(stored, key)) {
      _priceMem[key] = stored[key];
      return Promise.resolve(stored[key]);
    }
    /* 3. An identical request is already in flight: share it rather than
          firing a second one. */
    if (_priceInflight[key]) return _priceInflight[key];

    var p = SC.pricingApi.getPrice(payload).then(function(res) {
      var price = (!res || res.price == null || res.available === false) ? null : res.price;
      _priceMem[key] = price;
      /* Only persist real prices. A null may be a transient outage, and
         caching it would keep the card blank for the rest of the visit. */
      if (price != null) _priceStoreWrite(key, price);
      delete _priceInflight[key];
      return price;
    }).catch(function() {
      delete _priceInflight[key];
      return null;
    });

    _priceInflight[key] = p;
    return p;
  };

  /* ── Add a SAVED BUILD to the basket ──
     Same pricing-first contract as addPinToBasket, but saved builds
     already carry option slugs (range/size/fabric come from the quiz or
     the PDP), so no display-name resolution is needed. Falls back to
     opening the build's configurator link if no price comes back. */
  /* Build a configured-sofa basket entry.

     Field names and shapes must match what the configurator writes and
     what the drawer reads, or items render blank or not at all:
       - id must be unique per entry
       - sizeLabel is what the drawer shows in the title
       - image (not imageUrl) is the thumbnail
       - configSummary is a list of { label, value } objects, not strings
     See renderSofaItem() in sc-cart.js. */
  SC.gallery._sofaEntry = function(build, price, image, shareUrl) {
    var rangeKey = (build.range || '').toLowerCase();
    var rules = (SC.configurationRules || {})[rangeKey];
    var rangeName = build.rangeName ||
      (rules && rules.rangeName) ||
      (rangeKey ? rangeKey.charAt(0).toUpperCase() + rangeKey.slice(1) : 'Sofa');

    /* Turn a slug into the label the configurator would show. */
    function labelFor(groupKey, value) {
      if (!value) return '';
      if (rules && rules.groups) {
        for (var i = 0; i < rules.groups.length; i++) {
          if (rules.groups[i].key !== groupKey) continue;
          var opt = rules.groups[i].options.filter(function(o){ return o.value === value; })[0];
          if (opt) return opt.label;
        }
      }
      return String(value).replace(/-/g, ' ').replace(/\b\w/g, function(c){ return c.toUpperCase(); });
    }

    var fabricName = build.fabricName ||
      (build.fabric && SC.fabrics && SC.fabrics[build.fabric] ? SC.fabrics[build.fabric].name : '') ||
      build.fabric || '';

    var summary = [];
    [['cushion','Cushion'], ['cover','Cover'], ['piping','Piping'],
     ['cushionFill','Cushion fill'], ['legStyle','Legs'], ['legFinish','Leg finish']
    ].forEach(function(pair) {
      var v = build[pair[0]];
      if (v) summary.push({ label: pair[1], value: labelFor(pair[0], v) });
    });

    return {
      id: 'sofa_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 8),
      rangeName: rangeName,
      rangeKey: rangeKey,
      size: build.size || '',
      sizeLabel: labelFor('size', build.size) || build.size || '',
      fabric: build.fabric || '',
      fabricName: fabricName,
      cushion: build.cushion || '',
      cover: build.cover || '',
      piping: build.piping || '',
      cushionFill: build.cushionFill || '',
      legStyle: build.legStyle || '',
      legFinish: build.legFinish || '',
      frame: build.frame || '',
      price: price,
      sku: build.sku || '',
      image: image || '',
      shareUrl: shareUrl || window.location.href,
      configSummary: summary,
      addedAt: new Date().toISOString()
    };
  };

  /* Confirm on the button and open the drawer. */
  SC.gallery._afterAdd = function(btnEl) {
    if (btnEl) {
      btnEl.disabled = false;
      btnEl.textContent = 'Added \u2713';
      setTimeout(function(){ btnEl.textContent = 'Add to basket'; }, 2000);
    }
    if (SC.cart && SC.cart.openDrawer) SC.cart.openDrawer();
  };

  SC.gallery.addBuildToBasket = function(build, btnEl) {
    if (!build) return;
    var openUrl = (SC.saves && SC.saves.buildShareUrl) ? SC.saves.buildShareUrl(build) : null;

    function fail(msg) {
      /* Never redirect: the card has its own "Open" link if the customer
         wants the configurator. */
      if (btnEl) {
        btnEl.disabled = false;
        btnEl.textContent = msg;
        setTimeout(function(){ btnEl.textContent = 'Add to basket'; }, 2600);
      }
    }

    /* _addConfiguredSofa, NOT cart.addItem: configured sofas live in the
       'sc_sofa_basket' store that the drawer renders. cart.addItem writes
       to the older 'sc_cart' key, which nothing reads any more. */
    if (!(SC.pricingApi && SC.pricingApi.getPrice && SC.cart && SC.cart._addConfiguredSofa)) {
      console.warn('[SC gallery] Pricing or cart unavailable on this page. ' +
        'Check sc-pricing-api.js is loaded for this template in theme.liquid.');
      fail('Unavailable');
      return;
    }

    if (btnEl) { btnEl.disabled = true; btnEl.textContent = 'Adding\u2026'; }

    /* Seed the unset options from the range defaults before pricing, so
       the basket entry describes exactly the configuration that was
       priced. Quiz-saved builds carry only range, size and fabric; without
       this the drawer would list no cushion, cover or legs against a price
       that assumed them. */
    var marketEl = document.querySelector('[data-sc-market]');
    var market = (marketEl && marketEl.getAttribute('data-sc-market') === 'US') ? 'US' : 'UK';
    var seeded = SC.gallery.completeSelection((build.range || '').toLowerCase(), {
      size: build.size, cushion: build.cushion, backCushion: build.backCushion,
      cover: build.cover, piping: build.piping, cushionFill: build.cushionFill,
      legStyle: build.legStyle, legFinish: build.legFinish, frame: build.frame
    }, market);

    var full = {
      range: build.range, rangeName: build.rangeName,
      size: seeded.size || build.size, fabric: build.fabric,
      fabricName: build.fabricName, sku: build.sku,
      cushion: seeded.cushion, backCushion: seeded.backCushion,
      cover: seeded.cover, piping: seeded.piping,
      cushionFill: seeded.cushionFill, legStyle: seeded.legStyle,
      legFinish: seeded.legFinish, frame: seeded.frame
    };

    SC.gallery.priceBuild(full).then(function(price) {
      if (price == null) {
        console.warn('[SC gallery] No price returned for this build.', build);
        fail('Price on request');
        return;
      }
      SC.cart._addConfiguredSofa(
        SC.gallery._sofaEntry(full, price, build.imageUrl || '', openUrl));
      SC.gallery._afterAdd(btnEl);
    });
  };

  /* Tap any pin to view the full-resolution image. The favourite heart
     stays accessible inside the lightbox and stays in sync with the grid. */
  SC.gallery.openLightbox = function(itemId) {
    var item = (SC.gallery.items || []).filter(function(it){ return it.id === itemId; })[0];
    if (!item) return;
    var existing = document.querySelector('.sc-gallery-lightbox');
    if (existing) existing.remove();

    var full = item.imageLightbox || item.image;
    var isFav = SC.gallery.favourites.has(item.id);
    var configLink = SC.gallery.resolveConfigLink(item);
    var ctaHtml = '';
    if (configLink) {
      ctaHtml =
        '<div class="sc-gallery-lightbox-ctas">' +
          '<a class="sc-gallery-lightbox-configure" href="' + configLink.url + '">' +
            'See it on the sofa builder <span aria-hidden="true">&rsaquo;</span>' +
          '</a>' +
          '<button class="sc-gallery-lightbox-basket" type="button" data-sc-pin-basket="' + item.id + '">' +
            'Add to basket' +
          '</button>' +
        '</div>';
    }
    var lb = document.createElement('div');
    lb.className = 'sc-gallery-lightbox';
    lb.innerHTML =
      '<div class="sc-gallery-lightbox-inner">' +
        '<div class="sc-gallery-lightbox-stage">' +
          '<img class="sc-gallery-lightbox-img" src="' + full + '" alt="' + (item.alt || '') + '" />' +
          '<button class="sc-pin-save sc-gallery-lightbox-save' + (isFav ? ' is-saved' : '') + '" type="button" ' +
                  'aria-label="' + (isFav ? 'Remove from saved' : 'Save to favourites') + '" ' +
                  'aria-pressed="' + (isFav ? 'true' : 'false') + '" ' +
                  'data-sc-pin-save="' + item.id + '">' +
            '<svg class="sc-pin-heart" width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">' +
              '<path d="M12 21s-7.5-4.5-9.5-9.5C1 7 4 4 7.5 4c2 0 3.5 1 4.5 2.5C13 5 14.5 4 16.5 4 20 4 23 7 21.5 11.5 19.5 16.5 12 21 12 21z" ' +
                'fill="' + (isFav ? 'currentColor' : 'none') + '" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>' +
            '</svg>' +
          '</button>' +
          '<button class="sc-gallery-lightbox-close" type="button" aria-label="Close">&times;</button>' +
        '</div>' +
        '<div class="sc-gallery-lightbox-caption">' +
          '<span class="sc-gallery-lightbox-range">' + (item.range || '') + '</span>' +
          (item.fabric ? '<span class="sc-gallery-lightbox-fabric"> &middot; ' + item.fabric + '</span>' : '') +
        '</div>' +
        ctaHtml +
      '</div>';
    document.body.appendChild(lb);
    document.body.style.overflow = 'hidden';

    function close(){ lb.remove(); document.body.style.overflow = ''; document.removeEventListener('keydown', onKey); }
    function onKey(e){ if (e.key === 'Escape') close(); }
    document.addEventListener('keydown', onKey);
    lb.addEventListener('click', function(e){
      var basketBtn = e.target.closest('[data-sc-pin-basket]');
      if (basketBtn) { SC.gallery.addPinToBasket(item, basketBtn); return; }
      if (e.target === lb || e.target.closest('.sc-gallery-lightbox-close')) { close(); return; }
      /* The heart (data-sc-pin-save) is handled by the single site-wide click
         handler in bindSiteWide, which toggles once and then syncs every heart
         for this id (grid card + lightbox) plus the FAB via 'sc:favourites-changed'.
         Toggling here as well would fire twice and cancel out. */
    });
  };

  /* Tap a pin image to open the lightbox (but not when tapping the heart). */
  if (typeof document !== 'undefined') {
    document.addEventListener('click', function(e){
      if (e.target.closest('[data-sc-pin-save]')) return;      /* heart handled elsewhere */
      if (e.target.closest('.sc-gallery-lightbox')) return;            /* clicks inside lightbox */
      var pin = e.target.closest('.sc-pin[data-pin-id]');
      if (pin) SC.gallery.openLightbox(pin.getAttribute('data-pin-id'));
    });

    var st = document.createElement('style');
    st.textContent =
      '.sc-pin{cursor:pointer}' +
      '.sc-pin-image-wrap{position:relative}' +
      '.sc-gallery-lightbox{position:fixed;inset:0;background:rgba(18,22,30,.86);z-index:10000;display:flex;align-items:center;justify-content:center;padding:24px}' +
      '.sc-gallery-lightbox-inner{position:relative;max-width:94vw;max-height:94vh;display:flex;flex-direction:column;align-items:center}' +
      '.sc-gallery-lightbox-stage{position:relative;display:inline-block;max-width:94vw;max-height:82vh}' +
      '.sc-gallery-lightbox-img{display:block;max-width:94vw;max-height:82vh;width:auto;height:auto;border-radius:4px;box-shadow:0 12px 48px rgba(0,0,0,.4)}' +
      '.sc-gallery-lightbox-save{position:absolute;top:12px;right:12px;background:rgba(251,248,241,.92);border:none;border-radius:999px;width:42px;height:42px;display:flex;align-items:center;justify-content:center;cursor:pointer;color:#1B2D4A}' +
      /* sc.css hides .sc-pin-save until .sc-pin:hover on desktop; the lightbox
         heart isn't inside a pin, so force it visible there. */
      '.sc-gallery-lightbox .sc-pin-save{opacity:1;transform:none}' +
      '.sc-gallery-lightbox-close{position:absolute;top:12px;left:12px;width:42px;height:42px;display:flex;align-items:center;justify-content:center;font-size:26px;line-height:1;background:rgba(251,248,241,.92);border:none;border-radius:999px;color:#1B2D4A;cursor:pointer}' +
      '.sc-gallery-lightbox-caption{margin-top:14px;color:#FBF8F1;font-size:14px;letter-spacing:.04em;text-align:center}' +
      '.sc-gallery-lightbox-ctas{margin-top:12px;display:flex;gap:12px;align-items:center;justify-content:center;flex-wrap:wrap}' +
      '.sc-gallery-lightbox-configure{color:#FBF8F1;font-size:13px;letter-spacing:.06em;text-decoration:underline;text-underline-offset:3px}' +
      '.sc-gallery-lightbox-configure:hover{color:#C9A85C}' +
      '.sc-gallery-lightbox-basket{background:#C9A85C;color:#1B2D4A;border:none;border-radius:2px;padding:9px 18px;font-size:12px;letter-spacing:.1em;text-transform:uppercase;cursor:pointer}' +
      '.sc-gallery-lightbox-basket:hover{background:#D4B76E}' +
      '.sc-gallery-lightbox-basket:disabled{opacity:.6;cursor:default}' +
      '.sc-gallery-lightbox-range{font-weight:600}' +
      '@media (max-width:600px){.sc-gallery-lightbox-img{max-height:74vh}.sc-gallery-lightbox-stage{max-height:74vh}}';
    document.head.appendChild(st);
  }

  /* ---------------- MANIFEST LOADER (Cloudflare Worker) ---------------- */
  /* The gallery's pin list is served by a Worker that reads the R2 bucket
     directly: the team drags a correctly-named image into gallery/lightbox/
     in the Cloudflare dashboard and the page picks it up automatically.
     Falls back to the bundled SC.gallery.items if the fetch fails. */
  SC.gallery.loadFromManifest = function(url) {
    if (!url) return Promise.resolve(null);
    return fetch(url).then(function(r){
      if (!r.ok) throw new Error('manifest ' + r.status);
      return r.json();
    }).then(function(data){
      var items = (data && data.items) || [];
      if (items.length) SC.gallery.items = items;
      return items.length ? items : null;
    }).catch(function(e){
      console.warn('Gallery manifest unavailable, using bundled data.', e);
      return null;
    });
  };

  /* ---------------------- FILTERS ---------------------- */
  /* Range filters, derived from the data so a chip only appears when there
     are pins for that range. RANGE_ORDER sets the preferred display order;
     any range present in the data but not listed here is appended after,
     so nothing silently disappears (e.g. "Tables & props"). */
  var RANGE_ORDER = ['Coco', 'Felix', 'Isobel', 'Barton', 'Henrietta', 'Sophia'];

  function slugify(s) {
    return String(s).toLowerCase().replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }

  /* ── Top-level views ──
     The gallery page is three rooms rather than one long wall:
       our-gallery      our own photography
       customer-rooms   photographs customers have sent in
       bespoke-renders  the Render Room (its own dataset and grid,
                        drawn by sc-render-room.js)
     A view is chosen by the three large buttons above the chip row. The
     range chips then filter *within* whichever view is open, so a pin
     only ever appears in one room.

     To add another room later: add a row here, set the matching flag on
     the pins in captions.json, and add a button in sc-gallery.liquid. */
  SC.gallery.VIEWS = [
    { key: 'our-gallery', label: 'Our Gallery',
      test: function(it){ return !inAnyCollection(it); } },
    { key: 'customer-rooms', label: 'Customer Rooms',
      test: function(it){ return it.customerImage === true || it.group === 'customer'; } },
    /* This room draws from SC.renderRoom, not from the pin dataset, so it
       has no test: it never claims a pin. That matters — a room that both
       claimed pins and drew its own grid would swallow them silently. */
    { key: 'bespoke-renders', label: 'Bespoke Renders', renderRoom: true }
  ];

  /* A pin belongs to a "collection" when a later room claims it, and is
     then held out of Our Gallery so it does not appear twice. Rooms with
     no test claim nothing, so no pin can go missing. */
  function inAnyCollection(it) {
    var v = SC.gallery.VIEWS;
    for (var i = 1; i < v.length; i++) {
      if (v[i].test && v[i].test(it)) return true;
    }
    return false;
  }

  SC.gallery.view = 'our-gallery';

  SC.gallery.viewByKey = function(key) {
    return SC.gallery.VIEWS.filter(function(v){ return v.key === key; })[0] || null;
  };

  /* Membership test for a view, used to scope both the grid and the
     range chips. An unknown key falls back to the default view; a room
     that draws its own grid matches no pins. */
  function viewTest(key) {
    var v = SC.gallery.viewByKey(key) || SC.gallery.VIEWS[0];
    return v.test || function(){ return false; };
  }

  /* Chips for one view: 'All', then every range that has pins in it. */
  SC.gallery.buildFilters = function(items, viewKey) {
    items = items || SC.gallery.items || [];
    var inView = viewTest(viewKey || SC.gallery.view);
    var scoped = items.filter(inView);

    /* Which ranges actually have pins in this view, and how many. */
    var counts = {};
    scoped.forEach(function(it){
      var r = it.range || '';
      if (r) counts[r] = (counts[r] || 0) + 1;
    });
    var present = Object.keys(counts);
    /* Ordered: known ranges first (in RANGE_ORDER), then any extras A->Z. */
    var ordered = RANGE_ORDER.filter(function(r){ return counts[r]; })
      .concat(present.filter(function(r){ return RANGE_ORDER.indexOf(r) === -1; }).sort());

    var filters = [{ key: 'all', label: 'All', test: inView }];

    ordered.forEach(function(r){
      filters.push({
        key: slugify(r),
        label: r,
        range: r,
        test: (function(rangeName){
          return function(it){
            return it.range === rangeName && inView(it);
          };
        })(r)
      });
    });

    return filters;
  };

  /* Built once from the loaded dataset. Rebuild via buildFilters() if the
     dataset is swapped at runtime, or the view changes. */
  var FILTERS = SC.gallery.buildFilters();
  SC.gallery.FILTERS = FILTERS;

  SC.gallery.applyFilter = function(filterKey) {
    var list = SC.gallery.FILTERS || FILTERS;
    var f = list.filter(function(x){ return x.key === filterKey; })[0] || list[0];
    return SC.gallery.items.filter(f.test);
  };

  /* Paint the range chips for the current view into a mount, keeping the
     active chip if it still exists in the rebuilt set. */
  SC.gallery.paintFilters = function(mountId, activeKey) {
    var mount = document.getElementById(mountId || 'gallery-filters-mount');
    if (!mount) return 'all';
    SC.gallery.FILTERS = SC.gallery.buildFilters();
    var keys = SC.gallery.FILTERS.map(function(f){ return f.key; });
    var active = keys.indexOf(activeKey) !== -1 ? activeKey : 'all';
    mount.innerHTML = SC.gallery.FILTERS.map(function(f){
      return '<button type="button" class="sc-filter-chip' +
        (f.key === active ? ' is-active' : '') + '" ' +
        'data-sc-filter="' + f.key + '">' + f.label + '</button>';
    }).join('');
    return active;
  };

  /* Switch top-level view. Swaps which grid is on screen, rebuilds the
     range chips for that view, and reflects the choice in the URL so the
     view can be linked to. */
  SC.gallery.setView = function(key, updateUrl) {
    var view = SC.gallery.viewByKey(key);
    if (!view) return false;
    SC.gallery.view = view.key;

    document.querySelectorAll('[data-sc-view]').forEach(function(btn){
      var on = btn.getAttribute('data-sc-view') === view.key;
      btn.classList.toggle('is-active', on);
      btn.setAttribute('aria-selected', on ? 'true' : 'false');
    });

    var chipBar = document.querySelector('.sc-gallery-filters');
    var grid = document.getElementById('gallery-mount');
    var room = document.getElementById('render-room');

    if (view.renderRoom) {
      if (chipBar) chipBar.hidden = true;
      if (grid) grid.hidden = true;
      if (room) {
        room.hidden = false;
        /* Mounted on first open, then repainted. The Render Room's
           images are lazy, so nothing downloads until this runs. */
        if (SC.renderRoom && SC.renderRoom.mount) SC.renderRoom.mount('render-room-mount');
      }
    } else {
      if (room) room.hidden = true;
      if (chipBar) chipBar.hidden = false;
      if (grid) grid.hidden = false;
      var active = SC.gallery.paintFilters('gallery-filters-mount', 'all');
      SC.gallery.render('gallery-mount', SC.gallery.applyFilter(active));
      if (SC.rewriteAssetPaths) SC.rewriteAssetPaths();
    }

    if (updateUrl && window.history && history.replaceState) {
      var u = new URL(window.location.href);
      /* "room", not "view" — Shopify reserves ?view= for alternate
         templates and swallows the request before this file ever loads. */
      u.searchParams.delete('view');
      if (view.key === SC.gallery.VIEWS[0].key) u.searchParams.delete('room');
      else u.searchParams.set('room', view.key);
      u.hash = '';
      history.replaceState({}, '', u.toString());
    }
    return true;
  };

  /* ---------------------- FAVOURITES OVERLAY ---------------------- */
  SC.gallery.openFavourites = function() {
    var allFavourites = SC.gallery.favourites.items();
    var savedBuilds = (SC.saves && SC.saves.getBuilds) ? SC.saves.getBuilds() : [];

    /* A configured sofa hearted on a product page carries isBuild + a full
       build object. Show those as proper build cards (render shown whole, full
       spec) alongside any "Save & share" builds. Plain gallery pins stay as
       favourite cards. */
    var buildFavs = allFavourites.filter(function(f){ return f && f.isBuild && f.build; });
    var items = allFavourites.filter(function(f){ return !(f && f.isBuild && f.build); });
    var builds = savedBuilds.concat(buildFavs.map(function(f){
      /* Tag with the favourite's pin id so the card's remove button removes
         the favourite (not a saved build), and a share id for the Open link. */
      var bo = {};
      for (var k in f.build) bo[k] = f.build[k];
      bo.favId = f.id;
      bo.isFavBuild = true;
      return bo;
    }));

    var existing = document.querySelector('.sc-fav-overlay');
    if (existing) existing.remove();

    /* Saved builds first (higher intent), then favourites. */
    var buildsHtml = '';
    if (builds.length) {
      buildsHtml =
        '<section class="sc-fav-section sc-fav-section-builds">' +
          '<div class="sc-fav-section-head">' +
            '<div class="sc-eyebrow">Your saved builds</div>' +
            '<p class="sc-fav-section-sub">Pick up exactly where you left off.</p>' +
          '</div>' +
          '<div class="sc-fav-grid">' +
            builds.map(function(b){ return SC.gallery.buildCard(b); }).join('') +
          '</div>' +
        '</section>';
    }

    var favsHtml = '';
    if (items.length) {
      favsHtml =
        '<section class="sc-fav-section sc-fav-section-favs">' +
          (builds.length ? '<div class="sc-fav-section-head"><div class="sc-eyebrow">Your favourites</div><p class="sc-fav-section-sub">The ones you kept coming back to.</p></div>' : '') +
          '<div class="sc-fav-grid">' +
            items.map(function(it){ return SC.gallery.card(it); }).join('') +
          '</div>' +
        '</section>';
    }

    var bodyHtml;
    if (!items.length && !builds.length) {
      bodyHtml =
        '<div class="sc-fav-empty">' +
          '<p class="sc-lede">Nothing saved yet. Tap the heart on anything you like, or save a build from any sofa page, and it will land here.</p>' +
        '</div>';
    } else {
      bodyHtml = buildsHtml + favsHtml;
    }

    var countLabel = builds.length
      ? (builds.length + ' ' + (builds.length === 1 ? 'build' : 'builds') + ', ' + items.length + ' ' + (items.length === 1 ? 'favourite' : 'favourites'))
      : (items.length + ' saved');

    var overlay = document.createElement('div');
    overlay.className = 'sc-fav-overlay';
    overlay.innerHTML =
      '<div class="sc-fav-panel" role="dialog" aria-modal="true" aria-labelledby="sc-fav-title">' +
        '<header class="sc-fav-head">' +
          '<div>' +
            '<div class="sc-eyebrow">Your favourites</div>' +
            '<h2 id="sc-fav-title" class="sc-display-2">A few of your <em>favourite things</em>.</h2>' +
            '<p class="sc-fav-count">' + countLabel + '</p>' +
          '</div>' +
          '<button class="sc-icon-btn sc-fav-close" aria-label="Close" data-sc-fav-close>×</button>' +
        '</header>' +
        '<div class="sc-fav-actions">' +
          '<button class="sc-btn sc-btn-primary" type="button" data-sc-fav-pdf' + ((items.length === 0 && builds.length === 0) ? ' disabled' : '') + '>Download as PDF</button>' +
          
          (items.length > 0 ? '<button class="sc-btn-link sc-fav-clear" type="button" data-sc-fav-clear>Clear favourites</button>' : '') +
        '</div>' +
        '<div class="sc-fav-body">' + bodyHtml + '</div>' +
      '</div>';
    document.body.appendChild(overlay);
    document.body.style.overflow = 'hidden';
    /* Reveal */
    requestAnimationFrame(function(){ overlay.classList.add('is-open'); });
  };

  SC.gallery.closeFavourites = function() {
    var overlay = document.querySelector('.sc-fav-overlay');
    if (!overlay) return;
    overlay.classList.remove('is-open');
    document.body.style.overflow = '';
    setTimeout(function(){ overlay.remove(); }, 280);
  };

  /* Re-render the open overlay's body without the close/reopen animation.
     Used after removing a build or favourite from inside the overlay. */
  SC.gallery.refreshFavouritesOverlay = function() {
    var overlay = document.querySelector('.sc-fav-overlay.is-open');
    if (!overlay) return;
    /* Rebuild by reopening only if it would otherwise be empty/changed.
       Simplest robust approach: replace innerHTML of the panel body and
       the count line in place. */
    var allFavourites = SC.gallery.favourites.items();
    var savedBuilds = (SC.saves && SC.saves.getBuilds) ? SC.saves.getBuilds() : [];
    var buildFavs = allFavourites.filter(function(f){ return f && f.isBuild && f.build; });
    var items = allFavourites.filter(function(f){ return !(f && f.isBuild && f.build); });
    var builds = savedBuilds.concat(buildFavs.map(function(f){
      var bo = {};
      for (var k in f.build) bo[k] = f.build[k];
      bo.favId = f.id;
      bo.isFavBuild = true;
      return bo;
    }));

    var buildsHtml = '';
    if (builds.length) {
      buildsHtml =
        '<section class="sc-fav-section sc-fav-section-builds">' +
          '<div class="sc-fav-section-head">' +
            '<div class="sc-eyebrow">Your saved builds</div>' +
            '<p class="sc-fav-section-sub">Pick up exactly where you left off.</p>' +
          '</div>' +
          '<div class="sc-fav-grid">' +
            builds.map(function(b){ return SC.gallery.buildCard(b); }).join('') +
          '</div>' +
        '</section>';
    }
    var favsHtml = '';
    if (items.length) {
      favsHtml =
        '<section class="sc-fav-section sc-fav-section-favs">' +
          (builds.length ? '<div class="sc-fav-section-head"><div class="sc-eyebrow">Your favourites</div><p class="sc-fav-section-sub">The ones you kept coming back to.</p></div>' : '') +
          '<div class="sc-fav-grid">' +
            items.map(function(it){ return SC.gallery.card(it); }).join('') +
          '</div>' +
        '</section>';
    }
    var body = overlay.querySelector('.sc-fav-body');
    if (body) {
      body.innerHTML = (!items.length && !builds.length)
        ? '<div class="sc-fav-empty"><p class="sc-lede">Nothing saved yet. Tap the heart on anything you like, or save a build from any sofa page, and it will land here.</p></div>'
        : (buildsHtml + favsHtml);
    }
    var countEl = overlay.querySelector('.sc-fav-count');
    if (countEl) {
      countEl.textContent = builds.length
        ? (builds.length + ' ' + (builds.length === 1 ? 'build' : 'builds') + ', ' + items.length + ' ' + (items.length === 1 ? 'favourite' : 'favourites'))
        : (items.length + ' saved');
    }
    /* If everything is gone, disable the PDF button and drop Clear all */
    var pdfBtn = overlay.querySelector('[data-sc-fav-pdf]');
    if (pdfBtn) pdfBtn.disabled = (items.length === 0 && builds.length === 0);
    var clearBtn = overlay.querySelector('[data-sc-fav-clear]');
    if (clearBtn && (items.length + builds.length) === 0) clearBtn.remove();
  };

  /* Helper: lazy-load an external script (used for jsPDF). */
  function loadScript(url) {
    return new Promise(function(resolve, reject) {
      var s = document.createElement('script');
      s.src = url; s.onload = resolve; s.onerror = reject;
      document.head.appendChild(s);
    });
  }

  /* Helper: load an image so its bytes are reachable by canvas/addImage.
     CORS-tainted images can be decoded for display but NOT exported via
     toDataURL, which is why a render can show on the page yet come through
     blank in the PDF. We try two routes:
       1. fetch() the bytes as a blob and load via an object URL. When the
          host sends CORS headers this yields a fully canvas-safe image and
          is the most reliable path.
       2. fall back to an <img crossOrigin="anonymous"> load.
     Both resolve null on failure so a missing render never breaks the PDF
     (the build page shows a "Render to follow" panel instead). */
  function loadImageViaTag(url) {
    return new Promise(function(resolve) {
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function(){ resolve(img); };
      img.onerror = function(){ resolve(null); };
      var sep = url.indexOf('?') === -1 ? '?' : '&';
      img.src = url + sep + 'cors=1';
    });
  }

  function loadImage(url) {
    if (!url) return Promise.resolve(null);
    /* When the URL goes through Cloudflare Image Transformations
       (/cdn-cgi/image/<opts>/...) the transformed response doesn't carry
       the R2 bucket's CORS headers, so the canvas export fails. Strip the
       transform prefix to fetch the raw file from R2 directly — the PDF
       recompresses everything anyway, so the transform is wasted work here. */
    var cleanUrl = url.replace(/\/cdn-cgi\/image\/[^\/]+\//, '/');
    /* Route 1: fetch as blob → object URL → Image. */
    return fetch(cleanUrl, { mode: 'cors', cache: 'no-cache' })
      .then(function(resp){
        if (!resp.ok) throw new Error('fetch ' + resp.status);
        return resp.blob();
      })
      .then(function(blob){
        return new Promise(function(resolve){
          var objUrl = URL.createObjectURL(blob);
          var img = new Image();
          img.onload = function(){ resolve(img); /* keep object URL for the life of the PDF build */ };
          img.onerror = function(){ URL.revokeObjectURL(objUrl); resolve(null); };
          img.src = objUrl;
        });
      })
      .catch(function(){
        /* Route 2: classic crossOrigin <img>. */
        return loadImageViaTag(cleanUrl);
      });
  }

  /* Helper: page chrome (wordmark, page number, footer URL). Drawn once
     per page. */
  function drawPageChrome(doc, pageNum, W, H, MARGIN_X, MARGIN_BOTTOM, cormorantLoaded) {
    doc.setFillColor(251, 248, 241);
    doc.rect(0, 0, W, H, 'F');

    doc.setFont(cormorantLoaded ? 'Cormorant' : 'helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(27, 45, 74);
    doc.text('SOPHIE CONRAN', MARGIN_X, 14, { charSpace: 1.6 });

    doc.setTextColor(150, 145, 130);
    doc.setFontSize(8);
    doc.text(String(pageNum), W - MARGIN_X, 14, { align: 'right' });

    doc.setDrawColor(184, 154, 94);
    doc.setLineWidth(0.2);
    doc.line(MARGIN_X, H - MARGIN_BOTTOM + 6, W - MARGIN_X, H - MARGIN_BOTTOM + 6);

    doc.setFont(cormorantLoaded ? 'Cormorant' : 'times', 'italic');
    doc.setFontSize(9);
    doc.setTextColor(184, 154, 94);
    doc.text('sophieconran.com', W / 2, H - MARGIN_BOTTOM + 13, { align: 'center' });
  }

  /* Helper: embed Cormorant Garamond from local TTFs shipped with the
     prototype. Fall back to Times silently if the fetch fails. */
  async function embedCormorant(doc) {
    if (SC.gallery._cormorantCache && SC.gallery._cormorantCache.regular) {
      try {
        registerCormorant(doc, SC.gallery._cormorantCache);
        return true;
      } catch (e) { return false; }
    }
    try {
      var URLS = {
        regular: 'assets/fonts/cormorant-regular.ttf',
        italic:  'assets/fonts/cormorant-italic.ttf'
      };
      var regular = await fetchAsBase64(URLS.regular);
      var italic  = await fetchAsBase64(URLS.italic);
      if (!regular || !italic) return false;
      var cache = { regular: regular, italic: italic };
      SC.gallery._cormorantCache = cache;
      registerCormorant(doc, cache);
      return true;
    } catch (e) {
      console.warn('Cormorant embed failed, falling back to Times:', e);
      return false;
    }
  }

  function registerCormorant(doc, cache) {
    doc.addFileToVFS('Cormorant-Regular.ttf', cache.regular);
    doc.addFont('Cormorant-Regular.ttf', 'Cormorant', 'normal');
    doc.addFileToVFS('Cormorant-Italic.ttf', cache.italic);
    doc.addFont('Cormorant-Italic.ttf', 'Cormorant', 'italic');
  }

  function fetchAsBase64(url) {
    return fetch(url).then(function(r){
      if (!r.ok) throw new Error('font fetch failed: ' + r.status);
      return r.arrayBuffer();
    }).then(function(buf){
      var bytes = new Uint8Array(buf);
      var binary = '';
      for (var i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
      return btoa(binary);
    }).catch(function(){ return null; });
  }

  /* Helper: draw one configured-sofa summary page.
     Render letterboxed (object-fit: contain equivalent) at the top so the
     whole sofa shows, then a full spec list and the price. Used at the front
     of the saved-PDF, before the gallery favourites grid. */
  async function drawBuildPage(doc, build, img, W, H, MARGIN_X, MARGIN_TOP, cormorantLoaded) {
    var x = MARGIN_X;
    var contentW = W - MARGIN_X * 2;
    var y = MARGIN_TOP;

    /* Eyebrow + heading */
    doc.setFont(cormorantLoaded ? 'Cormorant' : 'helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(184, 154, 94);
    doc.text('YOUR CONFIGURATION', x, y, { charSpace: 1.4 });

    doc.setFont(cormorantLoaded ? 'Cormorant' : 'times', 'italic');
    doc.setFontSize(24);
    doc.setTextColor(27, 45, 74);
    var heading = (build.rangeName || build.range || 'Your sofa');
    if (build.size) heading += ' · ' + build.size;
    doc.text(heading, x, y + 11);

    var today = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
    doc.setFont(cormorantLoaded ? 'Cormorant' : 'times', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(74, 74, 74);
    doc.text('Configured ' + today, x, y + 19);

    doc.setDrawColor(184, 154, 94);
    doc.setLineWidth(0.25);
    doc.line(x, y + 24, x + contentW, y + 24);

    /* Render — letterboxed into a fixed box so the whole sofa is visible. */
    var boxY = y + 32;
    var boxH = 95;                 /* generous render area */
    var boxW = contentW;
    /* cream backing panel for the letterbox */
    doc.setFillColor(247, 241, 229);
    doc.rect(x, boxY, boxW, boxH, 'F');
    if (img) {
      /* contain: scale to fit inside the box, preserving aspect ratio */
      var ar = img.width / img.height;
      var drawW = boxW, drawH = boxW / ar;
      if (drawH > boxH) { drawH = boxH; drawW = boxH * ar; }
      var imgX = x + (boxW - drawW) / 2;
      var imgY = boxY + (boxH - drawH) / 2;
      try {
        var canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth || img.width;
        canvas.height = img.naturalHeight || img.height;
        canvas.getContext('2d').drawImage(img, 0, 0);
        doc.addImage(canvas.toDataURL('image/jpeg', 0.92), 'JPEG', imgX, imgY, drawW, drawH);
      } catch (e) { /* tainted/missing image — leave the cream panel */ }
    } else {
      doc.setFont(cormorantLoaded ? 'Cormorant' : 'times', 'italic');
      doc.setFontSize(11);
      doc.setTextColor(150, 145, 130);
      doc.text('Render to follow', x + boxW / 2, boxY + boxH / 2, { align: 'center' });
    }

    /* Spec list. The build stores RAW option values (so it can restore the
       configurator); resolve each to its display label here. Fabric is already
       a display name, kept as-is. */
    var bRules = (window.SC && SC.configurationRules && build.range) ? SC.configurationRules[build.range] : null;
    function specLabel(groupKey, rawValue) {
      if (!rawValue) return '';
      if (!bRules) return rawValue;
      var grp = bRules.groups.find(function(g){ return g.key === groupKey; });
      if (!grp) return rawValue;
      var opt = grp.options.find(function(o){ return o.value === rawValue; });
      return opt ? opt.label : rawValue;
    }
    var specY = boxY + boxH + 14;
    var rows = [
      ['Size', specLabel('size', build.size)],
      ['Fabric', build.fabricName || build.fabric],
      ['Seat cushion', specLabel('cushion', build.cushion)],
      ['Cover style', specLabel('cover', build.cover)],
      ['Piping', specLabel('piping', build.piping)],
      ['Cushion fill', specLabel('cushionFill', build.cushionFill)],
      ['Leg style', specLabel('legStyle', build.legStyle)],
      ['Leg finish', specLabel('legFinish', build.legFinish)],
      ['Frame', specLabel('frame', build.frame)]
    ].filter(function(r) { return r[1]; });

    doc.setFontSize(11);
    var lineH = 9;
    rows.forEach(function(r, i) {
      var ry = specY + i * lineH;
      /* label */
      doc.setFont(cormorantLoaded ? 'Cormorant' : 'helvetica', 'normal');
      doc.setTextColor(150, 145, 130);
      doc.text(String(r[0]).toUpperCase(), x, ry, { charSpace: 0.8 });
      /* value, right-aligned */
      doc.setFont(cormorantLoaded ? 'Cormorant' : 'times', 'normal');
      doc.setTextColor(27, 45, 74);
      doc.text(String(r[1]), x + contentW, ry, { align: 'right' });
      /* hairline */
      doc.setDrawColor(228, 221, 207);
      doc.setLineWidth(0.15);
      doc.line(x, ry + 2.5, x + contentW, ry + 2.5);
    });

    /* Price band */
    if (build.price) {
      var priceY = specY + rows.length * lineH + 8;
      doc.setFont(cormorantLoaded ? 'Cormorant' : 'helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(184, 154, 94);
      doc.text('YOUR BUILD', x, priceY, { charSpace: 1.2 });
      doc.setFont(cormorantLoaded ? 'Cormorant' : 'times', 'normal');
      doc.setFontSize(20);
      doc.setTextColor(27, 45, 74);
      doc.text('GBP ' + Number(build.price).toLocaleString('en-GB'), x + contentW, priceY + 1, { align: 'right' });
    }
  }

  /* ---------------------- PDF EXPORT (v3 grid) ----------------------
     Builds a print-quality PDF of the customer's saved items. Configured
     sofa build(s) come first as full spec pages, then the favourites grid:
     A4 portrait, the favourites pages have a YOUR FAVOURITES intro band + 2x2
     grid (4 cards), subsequent pages a clean 2x3 grid (6 cards). Card width is
     ~80mm — close to the web gallery's card density — with a small
     cream caption strip beneath the image.

     Layout constants. All in mm.
     A4 portrait: 210 x 297. Side margins 18mm each → content 174mm wide.
     Two columns + 8mm gutter → 83mm card width.
     Final favourites card: image height 60mm, caption 24mm, card 84mm.
     2x2 on the first favourites page (after the intro band), 2x3 after. */

  SC.gallery.downloadPDF = async function() {
    var allFavs = SC.gallery.favourites.items();
    var savedBuilds = (window.SC && SC.saves && SC.saves.getBuilds) ? SC.saves.getBuilds() : [];

    /* A configured sofa hearted on a product page carries isBuild + a full
       build object. Render those as proper build summary pages, alongside any
       builds saved via "Save & share". Plain gallery pins stay as grid cards. */
    var buildFavs = allFavs.filter(function(f){ return f && f.isBuild && f.build; });
    var plainFavs = allFavs.filter(function(f){ return !(f && f.isBuild && f.build); });
    var builds = savedBuilds.concat(buildFavs.map(function(f){ return f.build; }));

    /* Nothing saved at all → nothing to export. */
    if (plainFavs.length === 0 && builds.length === 0) return;
    var items = plainFavs;

    var btn = document.querySelector('[data-sc-fav-pdf]');
    if (btn) { btn.disabled = true; btn.textContent = 'Preparing your PDF…'; }

    try {
      if (!global.jspdf) {
        await loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js');
      }
      var jsPDF = global.jspdf.jsPDF;
      var doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait', compress: true });

      /* Embed Cormorant Garamond so the PDF reads like the site.
         Falls back to Times if the local TTF fetch fails. */
      var cormorantLoaded = await embedCormorant(doc);

      /* Page geometry */
      var W = 210, H = 297;
      var MARGIN_X = 18;
      var MARGIN_TOP = 28;
      var MARGIN_BOTTOM = 22;
      var CONTENT_W = W - MARGIN_X * 2;          /* 174mm */
      var COL_GAP = 8;
      var ROW_GAP = 8;
      var CARD_W = (CONTENT_W - COL_GAP) / 2;    /* 83mm */
      /* Card sizing — larger images, closer to how the gallery pins look on
         the site (taller, near-square). Caption strip below carries the name,
         swatch+fabric, divider, price+lead. */
      var IMG_H = 72;                            /* taller image at 83mm wide */
      var CAP_H = 22;                            /* tight caption strip */
      var CARD_H = IMG_H + CAP_H;                /* 94mm total */

      /* Page 1: intro band 32mm tall, then up to 2 rows of cards (4 cards).
         Page 2+: no intro, up to 3 rows of cards (6 cards). */
      var INTRO_H = 32;
      var page1Available = H - MARGIN_TOP - INTRO_H - MARGIN_BOTTOM;     /* 215mm */
      var pageNAvailable = H - MARGIN_TOP - MARGIN_BOTTOM;               /* 247mm */
      var rowsPage1 = Math.floor((page1Available + ROW_GAP) / (CARD_H + ROW_GAP));
      var rowsPageN = Math.floor((pageNAvailable + ROW_GAP) / (CARD_H + ROW_GAP));
      var perPage1 = rowsPage1 * 2;             /* 4 cards (with 215mm, fits 2 rows) */
      var perPageN = rowsPageN * 2;             /* 6 cards (with 247mm, fits 3 rows) */

      /* Pre-load favourites images so we can centre-crop to the card aspect. */
      var loaded = [];
      for (var i = 0; i < items.length; i++) {
        var img = await loadImage(items[i].imageLightbox || items[i].image);
        loaded.push({ item: items[i], img: img });
      }

      /* Chunk favourites into pages by capacity. */
      var pages = [];
      var idx = 0;
      while (idx < loaded.length) {
        var cap = pages.length === 0 ? perPage1 : perPageN;
        pages.push(loaded.slice(idx, idx + cap));
        idx += cap;
      }

      var pageNum = 0;

      /* ── Configured sofa build(s) first, one full spec page each ── */
      for (var b = 0; b < builds.length; b++) {
        if (pageNum > 0) doc.addPage();
        pageNum++;
        drawPageChrome(doc, pageNum, W, H, MARGIN_X, MARGIN_BOTTOM, cormorantLoaded);
        var buildImg = builds[b].imageUrl ? await loadImage(builds[b].imageUrl) : null;
        await drawBuildPage(doc, builds[b], buildImg, W, H, MARGIN_X, MARGIN_TOP, cormorantLoaded);
      }

      /* ── Then the gallery favourites grid ── */
      for (var p = 0; p < pages.length; p++) {
        if (pageNum > 0) doc.addPage();
        pageNum++;
        drawPageChrome(doc, pageNum, W, H, MARGIN_X, MARGIN_BOTTOM, cormorantLoaded);

        var gridY = MARGIN_TOP;
        if (p === 0) {
          drawIntroBand(doc, MARGIN_X, MARGIN_TOP, CONTENT_W, items.length, cormorantLoaded);
          gridY = MARGIN_TOP + INTRO_H;
        }

        var pageCards = pages[p];
        for (var c = 0; c < pageCards.length; c++) {
          var col = c % 2;
          var row = Math.floor(c / 2);
          var x = MARGIN_X + col * (CARD_W + COL_GAP);
          var y = gridY + row * (CARD_H + ROW_GAP);
          await drawGridCard(doc, pageCards[c].item, pageCards[c].img,
                             x, y, CARD_W, IMG_H, CAP_H, cormorantLoaded);
        }
      }

      /* Closing page */
      doc.addPage();
      pageNum++;
      drawPageChrome(doc, pageNum, W, H, MARGIN_X, MARGIN_BOTTOM, cormorantLoaded);
      drawClosingPage(doc, W, H, MARGIN_BOTTOM, cormorantLoaded);

      doc.save('sophie-conran-saved.pdf');
      if (btn) { btn.disabled = false; btn.textContent = 'Download as PDF'; }
      if (SC.account && SC.account.toast) {
        SC.account.toast('Your saved items are downloading.');
      }
    } catch (e) {
      console.error('PDF generation failed:', e);
      if (btn) { btn.disabled = false; btn.textContent = 'Download as PDF'; }
      if (SC.account && SC.account.toast) {
        SC.account.toast('Could not build the PDF. Please check your connection and try again.');
      } else {
        alert('Could not build the PDF. Please check your connection and try again.');
      }
    }
  };

  /* ---------------------------------------------------------------
     PDF LEAD GATE
     Collect name + email before the saved-edit PDF builds. Tagged
     source 'sc-saved-builds-pdf' so it can be told apart from the other
     captures (fabric picker, room quiz, catalogue) in Freshsales. Once a
     visitor has given their details the gate is skipped on later
     downloads, remembered like the catalogue request.
     --------------------------------------------------------------- */
  var PDF_LEAD_KEY = 'sc_pdf_lead';

  function pdfLeadOnRecord() {
    try {
      var d = JSON.parse(localStorage.getItem(PDF_LEAD_KEY) || 'null');
      return !!(d && d.email);
    } catch (e) { return false; }
  }

  /* Called from the button instead of downloadPDF: gate first, then build. */
  SC.gallery.requestPdf = function() {
    /* If the user has created an account and is signed in, capture the lead
       silently and go straight to the PDF — they've already given us their
       details. Everyone else sees the name/email gate every time. */
    if (window.SC && SC.saves && SC.saves.isSignedIn && SC.saves.isSignedIn()) {
      var u = SC.saves.getUser();
      if (u && u.email) {
        SC.gallery.capturePdfLead(u.name || u.firstName || '', u.email);
        SC.gallery.downloadPDF();
        return;
      }
    }
    SC.gallery.openPdfGate();
  };

  /* Build the lead payload in the SAME shape as the other captures and hand it
     to the CRM (Freshsales) via the Nadia worker's /api/leads route. The POST
     is non-blocking: a backend hiccup must never stop the download. */
  SC.gallery.capturePdfLead = function(name, email) {
    var nowIso = new Date().toISOString();
    try { localStorage.setItem(PDF_LEAD_KEY, JSON.stringify({ name: name, email: email, submittedAt: nowIso })); } catch (e) {}

    var allFavs = SC.gallery.favourites.items();
    var savedBuilds = (window.SC && SC.saves && SC.saves.getBuilds) ? SC.saves.getBuilds() : [];
    var buildFavs = allFavs.filter(function(f){ return f && f.isBuild && f.build; });
    var plainFavs = allFavs.filter(function(f){ return !(f && f.isBuild && f.build); });

    var payload = {
      source: 'sc-saved-builds-pdf',
      timestamp: nowIso,
      lead: { name: name, email: email },
      interest: {
        saved_items: plainFavs.map(function(f){ return { range: f.range || null, fabric: f.fabric || null }; }),
        saved_builds_count: savedBuilds.length + buildFavs.length,
        saved_builds: savedBuilds.concat(
          buildFavs.map(function(f){ return f.build || f; })
        ).map(function(b) {
          return {
            sku: b.sku || '',
            range: b.rangeName || b.range || '',
            size: b.size || '',
            fabric: b.fabricName || b.fabric || '',
            cushion: b.cushion || '',
            cover: b.cover || '',
            piping: b.piping || '',
            price: b.price || 0
          };
        })
      }
    };
    /* lead payload intentionally not logged in production (PII) */
    try {
      fetch('https://cdn.sophieconran.com/api/leads/saved-builds-pdf', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      }).catch(function(){});
    } catch (e) {}
  };

  /* The gate modal: name + email, sits above the favourites overlay. */
  SC.gallery.openPdfGate = function() {
    var existing = document.querySelector('.sc-pdf-gate');
    if (existing) existing.remove();

    function escAttr(v){ return String(v == null ? '' : v).replace(/&/g, '&amp;').replace(/"/g, '&quot;'); }
    var prior = {};
    try { prior = JSON.parse(localStorage.getItem(PDF_LEAD_KEY) || '{}') || {}; } catch (e) {}
    /* If they're signed in, pre-fill from the account too, so a known visitor
       only has to confirm and tap (their details are still captured as a lead). */
    if ((!prior.name || !prior.email) && window.SC && SC.account && SC.account.getCurrent) {
      var acc = SC.account.getCurrent();
      if (acc) {
        if (!prior.name)  prior.name  = acc.name  || prior.name;
        if (!prior.email) prior.email = acc.email || prior.email;
      }
    }

    var gate = document.createElement('div');
    gate.className = 'sc-pdf-gate';
    gate.innerHTML =
      '<div class="sc-pdf-gate-panel" role="dialog" aria-modal="true" aria-labelledby="sc-pdf-gate-title">' +
        '<button class="sc-icon-btn sc-pdf-gate-close" type="button" aria-label="Close" data-sc-pdf-gate-close>&times;</button>' +
        '<div class="sc-eyebrow">Your saved edit</div>' +
        '<h2 id="sc-pdf-gate-title" class="sc-pdf-gate-title">A PDF to <em>keep or share</em></h2>' +
        '<p class="sc-pdf-gate-lede">Tell us where to send it and we will put your saved pieces together as a PDF. We will only use your details to help with your order, and you can opt out of any notes from us whenever you like.</p>' +
        '<form class="sc-pdf-gate-form" novalidate>' +
          '<label class="sc-pdf-gate-field"><span>Name</span>' +
            '<input type="text" name="name" autocomplete="name" value="' + escAttr(prior.name) + '" required />' +
          '</label>' +
          '<label class="sc-pdf-gate-field"><span>Email address</span>' +
            '<input type="email" name="email" autocomplete="email" value="' + escAttr(prior.email) + '" required />' +
          '</label>' +
          '<p class="sc-pdf-gate-error" role="alert" hidden></p>' +
          '<button class="sc-btn sc-btn-primary sc-pdf-gate-submit" type="submit">Download PDF</button>' +
        '</form>' +
      '</div>';
    document.body.appendChild(gate);
    requestAnimationFrame(function(){ gate.classList.add('is-open'); });

    var nameInput  = gate.querySelector('input[name="name"]');
    var emailInput = gate.querySelector('input[name="email"]');
    var errEl      = gate.querySelector('.sc-pdf-gate-error');
    var form       = gate.querySelector('.sc-pdf-gate-form');
    if (nameInput) setTimeout(function(){ nameInput.focus(); }, 60);

    function close() {
      gate.classList.remove('is-open');
      setTimeout(function(){ if (gate.parentNode) gate.remove(); }, 220);
      document.removeEventListener('keydown', onKey);
    }
    function onKey(e){ if (e.key === 'Escape') close(); }
    document.addEventListener('keydown', onKey);

    function showError(msg) {
      if (!errEl) return;
      errEl.textContent = msg;
      errEl.hidden = false;
    }

    gate.addEventListener('click', function(e){
      if (e.target === gate || e.target.closest('[data-sc-pdf-gate-close]')) close();
    });

    form.addEventListener('submit', function(e){
      e.preventDefault();
      var name  = ((nameInput && nameInput.value) || '').trim();
      var email = ((emailInput && emailInput.value) || '').trim();
      if (!name) { showError('Please enter your name.'); if (nameInput) nameInput.focus(); return; }
      if (!/\S+@\S+\.\S+/.test(email)) { showError('Please enter a valid email address.'); if (emailInput) emailInput.focus(); return; }
      SC.gallery.capturePdfLead(name, email);
      close();
      SC.gallery.downloadPDF();
    });
  };

  /* Intro band on page 1: small gold eyebrow, italic display title,
     count + date, gold rule. Sized to fit in 32mm. */
  function drawIntroBand(doc, x, y, w, count, cormorantLoaded) {
    doc.setFont(cormorantLoaded ? 'Cormorant' : 'helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(184, 154, 94);
    doc.text('YOUR FAVOURITES', x, y, { charSpace: 1.4 });

    doc.setFont(cormorantLoaded ? 'Cormorant' : 'times', 'italic');
    doc.setFontSize(22);
    doc.setTextColor(27, 45, 74);
    doc.text('The pieces you kept coming back to.', x, y + 10);

    var today = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
    doc.setFont(cormorantLoaded ? 'Cormorant' : 'times', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(74, 74, 74);
    doc.text(count + ' saved · ' + today, x, y + 18);

    doc.setDrawColor(184, 154, 94);
    doc.setLineWidth(0.25);
    doc.line(x, y + 24, x + w, y + 24);
  }

  /* Draw a single grid card. Image on top, cream caption strip below.
     Image is centre-cropped to the card's image slot (landscape-ish, 4:3)
     so portraits and squares don't get letterboxed or stretched. */
  async function drawGridCard(doc, item, img, x, y, w, imgH, capH, cormorantLoaded) {
    /* Image area — fit each image at its OWN aspect ratio inside the fixed
       slot (letterbox), so portrait, square and landscape shots are all
       shown whole rather than force-cropped to one shape. The slot box stays
       a constant size to keep the grid aligned; the photo is centred in it. */
    var dataURL = null, drawW = w, drawH = imgH, dx = x, dy = y;
    if (img && img.naturalWidth && img.naturalHeight) {
      var nAspect = img.naturalHeight / img.naturalWidth;   /* h/w of source */
      var slotAspect = imgH / w;
      if (nAspect > slotAspect) {
        /* taller than slot -> height-bound, narrower width */
        drawH = imgH;
        drawW = imgH / nAspect;
      } else {
        /* wider than slot -> width-bound, shorter height */
        drawW = w;
        drawH = w * nAspect;
      }
      dx = x + (w - drawW) / 2;        /* centre horizontally */
      dy = y + (imgH - drawH) / 2;     /* centre vertically */
      dataURL = imageToDataURL(img, 1000);
    }
    /* Soft paper fill behind the image so letterbox margins read as
       intentional matting rather than gaps. */
    doc.setFillColor(248, 244, 236);
    doc.rect(x, y, w, imgH, 'F');
    if (dataURL) {
      try { doc.addImage(dataURL, 'JPEG', dx, dy, drawW, drawH); }
      catch (e) { fillSwatch(doc, item, x, y, w, imgH, cormorantLoaded); }
    } else {
      fillSwatch(doc, item, x, y, w, imgH, cormorantLoaded);
    }

    /* Caption strip — cream block, tight typography. */
    var capY = y + imgH;
    doc.setFillColor(245, 239, 227); /* cream */
    doc.rect(x, capY, w, capH, 'F');

    var padX = 6;
    var tx = x + padX;
    var rx = x + w - padX;

    /* Range name (Cormorant). Truncate so it never collides with the
       lead-time text on the right. */
    doc.setFont(cormorantLoaded ? 'Cormorant' : 'times', 'normal');
    doc.setFontSize(11);
    doc.setTextColor(27, 45, 74);
    /* Pre-measure the lead text width so we can reserve space */
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6);
    var leadPreviewText = leadLabel(item).toUpperCase();
    var leadPreviewCharSpace = 1.2;
    var leadPreviewW = doc.getTextWidth(leadPreviewText) + (leadPreviewText.length - 1) * leadPreviewCharSpace;
    /* Switch back to Cormorant for the name */
    doc.setFont(cormorantLoaded ? 'Cormorant' : 'times', 'normal');
    doc.setFontSize(11);
    var nameMaxW = w - padX * 2 - leadPreviewW - 3; /* 3mm safety gap */
    var name = truncate(doc, item.range, nameMaxW);
    doc.text(name, tx, capY + 7);

    /* Lead time (small caps, right-aligned).
       jsPDF has a known bug: text() with align:'right' AND charSpace does
       NOT account for the added inter-character spacing in its right-edge
       calculation, so the text overflows past the anchor. We compute the
       true width ourselves and place the text left-anchored at the
       correct x. */
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6);
    doc.setTextColor(122, 139, 111);
    var leadText = leadLabel(item).toUpperCase();
    var leadCharSpace = 1.2;
    var leadW = doc.getTextWidth(leadText) + (leadText.length - 1) * leadCharSpace;
    doc.text(leadText, rx - leadW, capY + 7, { charSpace: leadCharSpace });

    /* Swatch + fabric name */
    var rgb = hexToRgb(item.swatch || '#C5B098');
    doc.setFillColor(rgb.r, rgb.g, rgb.b);
    doc.circle(tx + 1.5, capY + 12, 1.5, 'F');
    doc.setDrawColor(220, 212, 195);
    doc.setLineWidth(0.12);
    doc.circle(tx + 1.5, capY + 12, 1.5, 'S');

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(74, 74, 74);
    var fabric = truncate(doc, item.fabric, w - padX * 2 - 6);
    doc.text(fabric, tx + 5, capY + 13);

    /* Hairline divider */
    doc.setDrawColor(216, 208, 190);
    doc.setLineWidth(0.12);
    doc.line(tx, capY + 16, rx, capY + 16);

    /* Price + Made to order */
    doc.setFont(cormorantLoaded ? 'Cormorant' : 'times', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(27, 45, 74);
    doc.text(priceLabel(item), tx, capY + 21);

    if (isFurniture(item)) {
      var priceW = doc.getTextWidth(priceLabel(item));
      doc.setFont(cormorantLoaded ? 'Cormorant' : 'times', 'italic');
      doc.setFontSize(7);
      doc.setTextColor(181, 106, 79);
      doc.text('Made to order', tx + priceW + 3, capY + 21);
    }
  }

  /* If text exceeds maxW at the current font, trim with an ellipsis. */
  function truncate(doc, text, maxW) {
    var t = String(text || '');
    if (doc.getTextWidth(t) <= maxW) return t;
    var ell = '…';
    while (t.length > 1 && doc.getTextWidth(t + ell) > maxW) t = t.slice(0, -1);
    return t + ell;
  }

  /* Closing "we are here" page — unchanged from v2 but pulled out for
     clarity. */
  function drawClosingPage(doc, W, H, MARGIN_BOTTOM, cormorantLoaded) {
    var midY = H / 2 - 20;
    doc.setFont(cormorantLoaded ? 'Cormorant' : 'times', 'italic');
    doc.setFontSize(20);
    doc.setTextColor(27, 45, 74);
    doc.text('When you are ready, we are here.', W / 2, midY, { align: 'center' });

    doc.setFont(cormorantLoaded ? 'Cormorant' : 'times', 'normal');
    doc.setFontSize(10.5);
    doc.setTextColor(74, 74, 74);
    var lines = [
      'Talk to Nadia, our designer concierge, any time at sophieconran.com.',
      'Order free fabric samples and try them in your room.',
      'Visit our showhome in London Bridge, by appointment.'
    ];
    for (var k = 0; k < lines.length; k++) {
      doc.text(lines[k], W / 2, midY + 14 + k * 7, { align: 'center' });
    }

    doc.setDrawColor(184, 154, 94);
    doc.setLineWidth(0.3);
    doc.line(W / 2 - 16, midY + 44, W / 2 + 16, midY + 44);

    doc.setFont(cormorantLoaded ? 'Cormorant' : 'times', 'italic');
    doc.setFontSize(11);
    doc.setTextColor(184, 154, 94);
    doc.text('Sophie', W / 2, midY + 54, { align: 'center' });
  }

  /* Centre-crop an image down to a target aspect ratio (h/w) and return
     a JPEG data URL. Used when an image is significantly taller than our
     cap aspect, to avoid wasted blank space and keep the composition. */
  /* Encode the whole image (no crop) to a JPEG data URL, capped at maxW
     on the long edge so the PDF stays light. Used for aspect-preserving
     placement where the image is fitted, not cropped. */
  function imageToDataURL(img, maxW) {
    if (!img) return null;
    var nW = img.naturalWidth, nH = img.naturalHeight;
    if (!nW || !nH) return null;
    var scale = Math.min(1, (maxW || 1000) / Math.max(nW, nH));
    var outW = Math.round(nW * scale), outH = Math.round(nH * scale);
    var c = document.createElement('canvas');
    c.width = outW; c.height = outH;
    var ctx = c.getContext('2d');
    try { ctx.drawImage(img, 0, 0, outW, outH); }
    catch (e) { return null; }
    try { return c.toDataURL('image/jpeg', 0.85); }
    catch (e) { return null; }
  }

  function centerCropToDataURL(img, targetAspect, maxW) {
    if (!img) return null;
    var nW = img.naturalWidth, nH = img.naturalHeight;
    if (!nW || !nH) return null;
    /* Compute crop window: full width, cropped height */
    var cropH = nW * targetAspect;
    if (cropH > nH) cropH = nH;
    var cropY = Math.max(0, (nH - cropH) / 2);
    /* Bias slightly upward for sofa shots so the sofa is centred rather
       than the floor — a small art-direction touch. */
    cropY = Math.max(0, cropY - cropH * 0.06);
    var outW = Math.min(nW, maxW || 1000);
    var outH = Math.round(outW * targetAspect);
    var c = document.createElement('canvas');
    c.width = outW;
    c.height = outH;
    var ctx = c.getContext('2d');
    try {
      ctx.drawImage(img, 0, cropY, nW, cropH, 0, 0, outW, outH);
    } catch (e) { return null; }
    try { return c.toDataURL('image/jpeg', 0.85); }
    catch (e) { return null; }
  }

  function fillSwatch(doc, item, x, y, w, h, cormorantLoaded) {
    var rgb = hexToRgb(item.swatch || '#C5B098');
    doc.setFillColor(rgb.r, rgb.g, rgb.b);
    doc.rect(x, y, w, h, 'F');
    doc.setFont(cormorantLoaded ? 'Cormorant' : 'times', 'italic');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(14);
    doc.text(item.range, x + w / 2, y + h / 2, { align: 'center' });
  }

  function hexToRgb(hex) {
    var c = hex.replace('#','');
    if (c.length === 3) c = c.split('').map(function(x){ return x+x; }).join('');
    return {
      r: parseInt(c.substr(0,2), 16),
      g: parseInt(c.substr(2,2), 16),
      b: parseInt(c.substr(4,2), 16)
    };
  }

  /* ---------------------- FILTER + URL ---------------------- */
  /* Both levels are reflected in the URL so any state can be linked to,
     bookmarked and shared:
       ?room=customer-rooms   the room (Our Gallery is the default, so it
                              carries no parameter)
       ?range=coco            the range chip inside that room
     A hash is also accepted for hand-written links and for Render Room
     set anchors (#coco-dg-ochre).

     NOTE: the room param is named "room", not "view". Shopify reserves
     ?view= on every resource to pick an alternate template (page.<value>
     .liquid); since no such template exists for our room keys, a fresh
     request to ?view=bespoke-renders never reaches this file at all —
     Shopify silently falls back to the page's default template instead.
     theme.liquid redirects any incoming ?view= to ?room=/?range= before
     this script runs, so don't reintroduce ?view= as the write target. */
  SC.gallery.selectFilter = function(key, updateUrl) {
    /* A top-level view key: switch rooms instead. */
    if (SC.gallery.viewByKey(key)) return SC.gallery.setView(key, updateUrl);

    var chips = document.querySelectorAll('[data-sc-filter]');
    var found = false;
    chips.forEach(function(el){
      var match = el.getAttribute('data-sc-filter') === key;
      el.classList.toggle('is-active', match);
      if (match) found = true;
    });
    if (!found) return false;

    SC.gallery.render('gallery-mount', SC.gallery.applyFilter(key));

    if (updateUrl && window.history && history.replaceState) {
      var u = new URL(window.location.href);
      if (key === 'all') u.searchParams.delete('range');
      else u.searchParams.set('range', key);
      u.hash = '';
      history.replaceState({}, '', u.toString());
    }
    return true;
  };

  /* Read ?room= / ?range= / #hash from the URL on load. Returns true when
     something was applied, so the caller knows not to fall back to the
     default room. ?view= is also read (but never written) as a fallback
     for any link theme.liquid's redirect didn't catch. */
  SC.gallery.applyFilterFromUrl = function() {
    var view = null, range = null;
    try {
      var params = new URL(window.location.href).searchParams;
      view = params.get('room') || params.get('view');
      range = params.get('range');
    } catch (e) { /* older browsers */ }

    /* Pre-views links used ?view= for range chips too. If the value is
       not a room, read it as a range. */
    if (view && !SC.gallery.viewByKey(view)) { range = range || view; view = null; }

    if (!view && location.hash && location.hash.length > 1) {
      var h = decodeURIComponent(location.hash.slice(1));
      /* ignore hashes that mean something else */
      if (h !== 'favourites' && h.indexOf('image-') !== 0) {
        if (SC.gallery.viewByKey(h)) view = h;
        else if (SC.renderRoom && SC.renderRoom.hasSet && SC.renderRoom.hasSet(h)) view = 'bespoke-renders';
        else range = range || h;
      }
    }

    var applied = false;
    if (view && SC.gallery.setView(view, false)) applied = true;
    if (view === 'bespoke-renders' && SC.renderRoom && SC.renderRoom.applyHash) {
      SC.renderRoom.applyHash();
    }
    if (range && SC.gallery.selectFilter(range, false)) applied = true;
    return applied;
  };

  /* ---------------------- TASTE FILTER ---------------------- */
  /* Someone who has been through the build quiz has already told us which
     sofa they are looking at. Rather than showing them a separate strip of
     guesses, open the gallery on that range.

     Rules: an explicit URL always wins, the chip has to exist, and the
     filtering is stated on the page with a one-tap way out. A silently
     filtered gallery would look like a gallery with far less in it. */
  SC.gallery.applyTasteFilter = function() {
    var url;
    try { url = new URL(window.location.href); } catch (e) { return false; }
    /* The visitor asked for something specific: leave it alone. */
    if (url.searchParams.get('room') || url.searchParams.get('view') || url.searchParams.get('range')) return false;
    if (location.hash && location.hash.length > 1) return false;

    var taste = null;
    try { taste = JSON.parse(localStorage.getItem('sc_taste') || 'null'); } catch (e) {}
    if (!taste || !taste.model) return false;

    /* Anyone who has already cleared it this session is not asked again. */
    try { if (sessionStorage.getItem('sc_gallery_taste_cleared') === '1') return false; } catch (e) {}

    var key = slugify(taste.model);
    var chip = document.querySelector('[data-sc-filter="' + key + '"]');
    if (!chip) return false;

    /* No explanatory band: the chip is visibly active and All sits right
       beside it, which says the same thing without a line of copy. */
    return SC.gallery.selectFilter(key, false);
  };

  /* ---------------------- PAGE BINDINGS ---------------------- */
  SC.gallery.bindPage = function() {
    var grid = document.getElementById('gallery-mount');
    var fab = document.querySelector('[data-sc-fav-fab]');
    var fabCount = document.querySelector('[data-sc-fav-fab-count]');

    function syncFab() {
      var n = SC.gallery.favourites.list().length;
      if (fabCount) {
        fabCount.textContent = n;
        fabCount.style.display = n > 0 ? '' : 'none';
      }
      if (fab) {
        fab.classList.toggle('has-favs', n > 0);
      }
    }
    syncFab();

    /* Top-level view buttons: Our Gallery / Customer Rooms / Bespoke
       Renders. Bound before the chips so a click on a view button never
       falls through to the chip handler. */
    document.addEventListener('click', function(e) {
      var btn = e.target.closest('[data-sc-view]');
      if (!btn) return;
      SC.gallery.setView(btn.getAttribute('data-sc-view'), true);
    });

    /* Filter chips (gallery-page only) */
    document.addEventListener('click', function(e) {
      var chip = e.target.closest('[data-sc-filter]');
      if (!chip) return;
      var key = chip.getAttribute('data-sc-filter');
      SC.gallery.selectFilter(key, true);
    });

    /* FAB click → open favourites */
    if (fab) {
      fab.addEventListener('click', function(){ SC.gallery.openFavourites(); });
    }

    /* FAB sync + pulse on save events */
    document.addEventListener('sc:favourites-changed', function() {
      syncFab();
      if (fab && SC.gallery.favourites.count() > 0) {
        fab.classList.add('pulse');
        setTimeout(function(){ fab.classList.remove('pulse'); }, 600);
      }
    });

    /* Land directly on a filtered view from a URL, else open on whatever
       the visitor built in the quiz. The URL is checked first so a shared
       link never gets overridden by the sharer's own quiz answers. */
    if (!SC.gallery.applyFilterFromUrl()) SC.gallery.applyTasteFilter();

    /* Open with hash #favourites */
    /* Deep-link to a specific image from the homepage teaser.
       The hash carries the gallery item ID (for auto-picked pins) or
       a CDN URL (for CMS-chosen images). Try ID first, then fall back
       to matching the image src attribute. */
    if (location.hash && location.hash.indexOf('#image-') === 0) {
      var targetVal = decodeURIComponent(location.hash.slice(7));
      setTimeout(function() {
        /* Try by pin ID first */
        var card = document.querySelector('[data-pin-id="' + targetVal + '"]');
        /* Fall back: match by image URL substring (CDN images) */
        if (!card) {
          document.querySelectorAll('.sc-pin[data-pin-id]').forEach(function(pin) {
            if (card) return;
            var img = pin.querySelector('img');
            if (img && img.src && img.src.indexOf(targetVal) !== -1) card = pin;
          });
        }
        /* Last resort: find the item in the data by image URL and get its ID */
        if (!card && SC.gallery && SC.gallery.items) {
          var match = SC.gallery.items.find(function(it) {
            return it.image && it.image.indexOf(targetVal) !== -1;
          });
          if (match) card = document.querySelector('[data-pin-id="' + match.id + '"]');
        }
        if (card) {
          card.scrollIntoView({ behavior: 'smooth', block: 'center' });
          card.click();
        }
      }, 800);
    }
    if (location.hash === '#favourites') {
      setTimeout(function(){ SC.gallery.openFavourites(); }, 100);
    }
  };

  /* ---------------------- ACCOUNT INTEGRATION ---------------------- */
  /* If signed in, this is a no-op (favourites already persist on device).
     If signed out, prompt sign-in then confirm. In production this is
     where we'd POST favourites to the server. */
  SC.gallery.saveToAccount = function() {
    /* Route through the synced saves system so favourites reach the Worker
       and become available on other devices. */
    if (SC.saves && SC.saves.requireSignIn) {
      var alreadyIn = SC.saves.isSignedIn && SC.saves.isSignedIn();
      SC.saves.requireSignIn(function() {
        /* Push current favourites as full objects now that we have an email */
        if (SC.saves.setFavouriteObjects) {
          try { SC.saves.setFavouriteObjects(SC.gallery.favourites.items()); } catch (e) {}
        }
        var u = SC.saves.getUser ? SC.saves.getUser() : null;
        var first = (u && u.firstName) ? u.firstName : 'there';
        if (SC.account && SC.account.toast) {
          SC.account.toast(alreadyIn
            ? 'Saved to your account, ' + first + '.'
            : 'Welcome, ' + first + '. Your favourites are saved.');
        }
        /* Refresh the header name/dropdown state if present */
        document.querySelectorAll('[data-sc-signin-name]').forEach(function(el) {
          if (u && u.firstName) el.textContent = u.firstName;
        });
      });
      return;
    }
    /* Legacy fallback (should not happen in the theme) */
    if (SC.account && SC.account.requireSignIn) {
      SC.account.requireSignIn(function() {
        var u = SC.account.getCurrent();
        if (SC.account.toast) SC.account.toast('Welcome, ' + (u.name || '').split(' ')[0] + '. Your favourites are saved.');
      });
    }
  };

  /* ---------------------- SITE-WIDE BINDINGS ---------------------- */
  /* These run on every page that includes sc-gallery.js. They:
       - turn the header heart icon into the favourites entry point
       - add a count badge next to it that updates live
       - show a one-time toast on the very first save, pointing to the FAB
         (or to the header heart, depending on context)
       - sync the heart fill state on PDP save hearts when navigating back
     Designed so favourites feel like a real, persistent feature rather
     than something you only find on the gallery page. */
  SC.gallery.bindSiteWide = function() {
    /* Guard against double-registration. This function is called from two
       places by design:
         - SC.page.boot() in sc-components.js (every page that boots chrome)
         - SC.gallery.bindHeaderWishlist() (legacy alias used by gallery.html)
       Both need to exist so older inline scripts keep working. But we MUST
       only register the document-level click listeners once, otherwise the
       save heart's toggle cancels itself out: every click fires two
       handlers, each calling favourites.toggle(id), net result zero.
       That was the regression that broke the "save in gallery" feedback. */
    if (SC.gallery._siteWideBound) return;
    SC.gallery._siteWideBound = true;

    /* If already signed in (persisted session, possibly a fresh device),
       pull favourites + builds from the Worker so they show without needing
       to sign in again. Applies via _applyFavourites, which updates badges. */
    if (SC.saves && SC.saves.isSignedIn && SC.saves.isSignedIn() && SC.saves.refreshFromServer) {
      SC.saves.refreshFromServer();
    }

    /* Header wishlist heart: opens favourites overlay and shows count */
    var hearts = document.querySelectorAll('.sc-icon-btn[aria-label="Wishlist"]');
    hearts.forEach(function(btn) {
      /* sc-saves.js may have already wired this; replace its handler
         with the gallery-aware version. */
      btn.addEventListener('click', function(e) {
        e.preventDefault();
        SC.gallery.openFavourites();
      });
      /* Attach a count badge if not already there */
      if (!btn.querySelector('.sc-header-fav-count')) {
        var badge = document.createElement('span');
        badge.className = 'sc-header-fav-count';
        badge.setAttribute('aria-hidden', 'true');
        btn.appendChild(badge);
      }
      btn.classList.add('sc-icon-btn-fav');
    });

    function syncHeaderBadge() {
      var favCount = SC.gallery.favourites.count();
      var buildCount = (SC.saves && SC.saves.getBuilds) ? SC.saves.getBuilds().length : 0;
      var total = favCount + buildCount;
      document.querySelectorAll('.sc-header-fav-count').forEach(function(b) {
        b.textContent = total > 0 ? String(total) : '';
        b.setAttribute('data-count', String(total));
      });
    }
    syncHeaderBadge();
    document.addEventListener('sc:favourites-changed', syncHeaderBadge);

    /* Footer "Your favourites" link: open overlay directly on every page
       except the gallery (where hash navigation will handle it). */
    document.querySelectorAll('[data-sc-fav-open-link]').forEach(function(link) {
      link.addEventListener('click', function(e) {
        if (document.body.getAttribute('data-sc-page') === 'gallery') return;
        e.preventDefault();
        SC.gallery.openFavourites();
      });
    });

    /* Save heart on any card (gallery card, lightbox or overlay card).
       This is the SINGLE place that toggles a favourite from a heart click,
       so the same click can never fire two toggles and cancel out. */
    document.addEventListener('click', function(e) {
      var btn = e.target.closest('[data-sc-pin-save]');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      SC.gallery.favourites.toggle(btn.getAttribute('data-sc-pin-save'));
      /* Visual state is applied by syncHearts() below, which runs on
         'sc:favourites-changed'. That keeps the grid card, the lightbox and
         any overlay card for the same id in sync, whichever one was clicked. */
    });

    /* Reflect the saved state on every heart on the page for a given id.
       Runs on any favourites change (heart click, overlay remove, sign-in
       sync), so hearts never drift out of sync with the store. */
    function syncHearts() {
      var els = document.querySelectorAll('[data-sc-pin-save]');
      Array.prototype.forEach.call(els, function(el) {
        var fav = SC.gallery.favourites.has(el.getAttribute('data-sc-pin-save'));
        el.classList.toggle('is-saved', fav);
        el.setAttribute('aria-pressed', fav ? 'true' : 'false');
        el.setAttribute('aria-label', fav ? 'Remove from saved' : 'Save to favourites');
        var p = el.querySelector('path');
        if (p) p.setAttribute('fill', fav ? 'currentColor' : 'none');
      });
    }
    document.addEventListener('sc:favourites-changed', syncHearts);

    /* Favourites overlay actions, also site-wide so the overlay works
       however it was opened (gallery FAB, header heart, footer link). */
    document.addEventListener('click', function(e) {
      /* Close: explicit close button OR clicking on the outer scrim */
      if (e.target.closest('[data-sc-fav-close]') ||
          (e.target.classList.contains('sc-fav-overlay') && !e.target.closest('.sc-fav-panel'))) {
        SC.gallery.closeFavourites();
      }
      if (e.target.closest('[data-sc-fav-pdf]')) {
        SC.gallery.requestPdf();
      }
      if (e.target.closest('[data-sc-fav-clear]')) {
        if (confirm('Clear all favourites? Your saved builds will be kept. This cannot be undone.')) {
          SC.gallery.favourites.clear();
          /* Keep the overlay open so any saved builds remain visible */
          SC.gallery.refreshFavouritesOverlay();
          /* If we're on the gallery page, also re-render the grid so any
             cards that were active reset their saved state. */
          if (document.getElementById('gallery-mount')) {
            var active = document.querySelector('[data-sc-filter].is-active');
            var key = active ? active.getAttribute('data-sc-filter') : 'all';
            SC.gallery.render('gallery-mount', SC.gallery.applyFilter(key));
          }
        }
      }
      if (e.target.closest('[data-sc-fav-save-account]')) {
        SC.gallery.saveToAccount();
      }
      /* Add a saved build to the basket, priced through the same endpoint
         the PDP uses. Looks in saved builds first, then favourite builds. */
      var buildBasket = e.target.closest('[data-sc-build-basket]');
      if (buildBasket) {
        e.preventDefault();
        var bbid = buildBasket.getAttribute('data-sc-build-basket');
        var b = null;
        if (SC.saves && SC.saves.getBuilds) {
          b = SC.saves.getBuilds().filter(function(x){ return x.id === bbid; })[0] || null;
        }
        if (!b && SC.saves && SC.saves.getFavouriteObjects) {
          b = SC.saves.getFavouriteObjects().filter(function(x){ return x && x.id === bbid; })[0] || null;
        }
        if (b) SC.gallery.addBuildToBasket(b, buildBasket);
        return;
      }
      /* Remove a saved build from the overlay */
      var buildRemove = e.target.closest('[data-sc-build-remove]');
      if (buildRemove) {
        e.preventDefault();
        var bid = buildRemove.getAttribute('data-sc-build-remove');
        if (SC.saves && SC.saves.deleteBuild) SC.saves.deleteBuild(bid);
        SC.gallery.refreshFavouritesOverlay();
      }
      /* Remove a hearted-from-PDP build (it lives in favourites). */
      var favRemove = e.target.closest('[data-sc-fav-remove]');
      if (favRemove) {
        e.preventDefault();
        var fid = favRemove.getAttribute('data-sc-fav-remove');
        if (SC.gallery.favourites && SC.gallery.favourites.remove) SC.gallery.favourites.remove(fid);
        SC.gallery.refreshFavouritesOverlay();
      }
    });

    /* Escape key closes the overlay */
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' && document.querySelector('.sc-fav-overlay.is-open')) {
        SC.gallery.closeFavourites();
      }
    });

    /* On any page, when a heart is clicked and it's the very first save
       on this device, show a one-time toast that tells the customer
       where their favourites live. Suppress once shown. */
    document.addEventListener('sc:favourites-changed', function() {
      var shown = localStorage.getItem('sc_fav_first_save_shown') === '1';
      if (shown) return;
      var n = SC.gallery.favourites.count();
      if (n !== 1) return;
      localStorage.setItem('sc_fav_first_save_shown', '1');
      showFirstSaveToast();
    });
  };

  /* Custom toast that's friendlier than the generic SC.account.toast.
     Anchors visually to the header heart icon on most pages, or the FAB
     on the gallery. Auto-dismisses after 5 seconds; click X to dismiss. */
  function showFirstSaveToast() {
    var existing = document.querySelector('.sc-fav-firstsave');
    if (existing) existing.remove();
    var anchor = document.querySelector('[data-sc-fav-fab]')
              || document.querySelector('.sc-icon-btn[aria-label="Wishlist"]');
    var anchorIsFab = anchor && anchor.matches('[data-sc-fav-fab]');
    var toast = document.createElement('div');
    toast.className = 'sc-fav-firstsave';
    if (anchorIsFab) toast.classList.add('sc-fav-firstsave--fab');
    toast.innerHTML =
      '<div class="sc-fav-firstsave-arrow" aria-hidden="true"></div>' +
      '<div class="sc-fav-firstsave-body">' +
        '<div class="sc-fav-firstsave-eyebrow">Saved</div>' +
        '<p>Your favourites live ' + (anchorIsFab ? 'in the button at the bottom-left.' : 'behind the heart at the top of the page.') + ' Tap it any time to see them, download as a PDF, or send to us.</p>' +
        '<button type="button" class="sc-btn sc-btn-primary sc-fav-firstsave-cta" data-sc-firstsave-see>See my saved</button>' +
      '</div>' +
      '<button class="sc-fav-firstsave-close" type="button" aria-label="Dismiss" data-sc-firstsave-close>×</button>';
    document.body.appendChild(toast);
    requestAnimationFrame(function(){ toast.classList.add('is-open'); });
    /* Auto-dismiss */
    var dismiss = function() {
      toast.classList.remove('is-open');
      setTimeout(function(){ if (toast.parentNode) toast.remove(); }, 320);
    };
    setTimeout(dismiss, 7000);
    toast.addEventListener('click', function(e) {
      if (e.target.closest('[data-sc-firstsave-close]')) {
        dismiss();
      } else if (e.target.closest('[data-sc-firstsave-see]')) {
        dismiss();
        SC.gallery.openFavourites();
      }
    });
  }

  /* Legacy alias, kept for the gallery page's existing init call */
  SC.gallery.bindHeaderWishlist = function() {
    SC.gallery.bindSiteWide();
  };

  /* ---------------------- PDP SAVE ---------------------- */
  /* Wires the save heart on the product configurator page.
     Reads the current configurator selection (range, fabric, price,
     image) and stores it as a custom pin in favourites. Each PDP-saved
     build gets a stable id based on its range + fabric combination, so
     re-clicking the heart unsaves the same build. */
  SC.gallery.bindPDPSave = function() {
    var btn = document.querySelector('[data-sc-pdp-save]');
    if (!btn) return;

    function currentPin() {
      if (!SC.configurator || !SC.configurator.getSelection) return null;
      var sel = SC.configurator.getSelection();
      if (!sel || !sel.range) return null;
      /* Build a stable id from the FULL configuration, not just fabric, so two
         builds that differ only in cushion / cover / piping / fill / legs /
         frame are recognised as different saves (and re-clicking the heart on
         the exact same configuration still toggles the same one off). */
      var idParts = [
        'pdp', sel.range, (sel.size || 'std'), (sel.fabric || 'default'),
        sel.cushion || '', sel.cover || '', sel.piping || '',
        sel.cushionFill || '', sel.legStyle || '', sel.legFinish || '', sel.frame || ''
      ];
      var id = idParts.join('-').replace(/-+/g, '-').replace(/-$/, '');
      var range = (SC.ranges || []).find(function(r){ return r.handle === sel.range; }) || {};
      var fabricObj = (SC.fabrics && SC.fabrics[sel.fabric]) || {};
      var img = SC.configurator.getCurrentImage();
      var price = SC.configurator.getCurrentPrice();

      /* Resolve each option value to its display label from the config rules,
         so the saved build reads "Knife edge" not "knife-edge". */
      var rules = (SC.configurationRules && range.handle) ? SC.configurationRules[range.handle] : null;
      function labelFor(groupKey, value) {
        if (!rules || !value) return value || '';
        var grp = rules.groups.find(function(g){ return g.key === groupKey; });
        if (!grp) return value;
        var opt = grp.options.find(function(o){ return o.value === value; });
        return opt ? opt.label : value;
      }
      var sizeLabel = labelFor('size', sel.size);

      /* Two different fabric labels for two different places:
         - the saved OVERVIEW card shows a friendly "Custom configuration"
           (listing every option there would be too much);
         - the PDF build summary has room for the real fabric name, which is
           more useful, so it gets the actual name (or "Your own fabric" when
           the customer genuinely chose COM with no named cloth). */
      var rawFabricName = fabricObj.name || '';
      var isCom = sel.fabric === 'bespoke' || /own material|own fabric/i.test(rawFabricName);
      var cardFabricLabel = (isCom || !rawFabricName) ? 'Custom configuration' : rawFabricName;
      var pdfFabricName = rawFabricName ? rawFabricName : (isCom ? 'Your own fabric' : 'Custom configuration');

      return {
        id: id,
        range: range.name + (sizeLabel ? ' ' + sizeLabel : ''),
        fabric: cardFabricLabel,
        image: (img && img.src) || '',
        alt: (img && img.alt) || (range.name || '') + ' in ' + (rawFabricName || 'custom configuration'),
        priceFrom: price || range.fromPrice || null,
        weeks: range.leadTimeWeeks || 8,
        swatch: fabricObj.swatchColour || '#C5B098',
        tags: ['sofas', 'pdp-build'],
        source: 'pdp',
        /* Full configuration carried so the PDF can render a proper build
           summary page (render + full spec) and so opening the build can
           restore the configurator. Stored as RAW values (like saveBuild), so
           both build types share one shape and both restore cleanly; the PDF
           resolves display labels itself from the config rules. */
        isBuild: true,
        build: {
          rangeName: range.name || '',
          range: range.handle || sel.range,
          size: sel.size || '',
          fabric: sel.fabric || '',
          fabricName: pdfFabricName,
          cushion: sel.cushion || '',
          cover: sel.cover || '',
          piping: sel.piping || '',
          cushionFill: sel.cushionFill || '',
          legStyle: sel.legStyle || '',
          legFinish: sel.legFinish || '',
          frame: sel.frame || '',
          price: price || 0,
          imageUrl: (img && img.src) || '',
          /* The product page this build was configured on, so opening it later
             from anywhere (e.g. the account page) returns to the right sofa. */
          pageUrl: window.location.origin + window.location.pathname
        }
      };
    }

    function refreshState() {
      var pin = currentPin();
      if (!pin) return;
      var saved = SC.gallery.favourites.has(pin.id);
      btn.classList.toggle('is-saved', saved);
      btn.setAttribute('aria-pressed', saved ? 'true' : 'false');
      var label = btn.querySelector('.sc-pdp-save-label');
      if (label) label.textContent = saved ? 'Saved' : 'Save';
      var path = btn.querySelector('svg path');
      if (path) path.setAttribute('fill', saved ? 'currentColor' : 'none');
    }
    refreshState();

    btn.addEventListener('click', function() {
      var pin = currentPin();
      if (!pin) return;
      if (SC.gallery.favourites.has(pin.id)) {
        SC.gallery.favourites.remove(pin.id);
      } else {
        SC.gallery.favourites.addCustom(pin);
        /* Brief celebratory pulse */
        btn.classList.add('pulse');
        setTimeout(function(){ btn.classList.remove('pulse'); }, 600);
      }
      refreshState();
    });

    /* Re-evaluate state when configurator changes (fabric, size, etc.)
       so the heart shows correctly for whatever build is currently up. */
    document.addEventListener('click', function(e) {
      if (e.target.closest('[data-sc-config-group]') ||
          e.target.closest('[data-sc-fabric-handle]') ||
          e.target.closest('[data-sc-gallery-pick]')) {
        setTimeout(refreshState, 50);
      }
    });
    document.addEventListener('sc:favourites-changed', refreshState);
  };

})(typeof window !== 'undefined' ? window : this);
